import { useState, useRef, useCallback, useEffect } from "react";
import { Link } from "react-router";
import { useRelativePath, useTemplateType } from "./TemplateContext";
import { useDrag, useDrop } from "react-dnd";
import { useContentEditingSafe } from "./ContentEditingContext";
import { DND_PALETTE_BLOCK, DND_ZONE_BLOCK, ALL_BLOCK_TYPES, getBlocksForType, useIsDraggingAny } from "./BlockPalette";
import { saveSection as saveSectionToLib } from "../../utils/savedSectionsApi";
import { useSavedSections } from "./BlockPalette";
import {
  Plus,
  Trash2,
  Copy,
  ChevronUp,
  ChevronDown,
  Type,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  Quote,
  Info,
  Minus,
  AlertTriangle,
  CheckCircle2,
  Lightbulb,
  X,
  GripVertical,
  Search,
  Bookmark,
  Video,
  ExternalLink,
  Play,
  MessageSquare,
  Table2,
  MoveHorizontal,
  Clock,
  Tag,
  Check,
  Bell,
  Circle,
  Users,
  Timer,
  Share2,
  Pause,
  ClipboardCopy,
  ClipboardPaste,
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import {
  SectionBlockRenderer,
  isSectionType,
  getSectionDefaultContent,
  getSectionDefaultMeta,
} from "./SectionBlocks";
import { IconEmojiField } from "./IconEmojiPicker";
import { SectionDropSlotProvider } from "./SectionDropSlotContext";
import { PageLinkPicker } from "./PageLinkPicker";
import { EditableImage } from "./MediaPickerModal";
import { handleFormatKeyDown as blockFormatKeyDown, extractRichValue, hasHtmlFormatting } from "./InlineFormatToolbar";
import { BlockFormatBar } from "./BlockFormatBar";
import type { TypoData } from "./BlockFormatBar";
import { copyBlockToClipboard, getClipboardBlocks, hasClipboardBlock, notifyClipboardChange, onClipboardChange } from "../../utils/blockClipboard";

/* ───────────── Types ───────────── */

export type { ContentBlock } from "./contentBlockTypes";
import type { ContentBlock } from "./contentBlockTypes";

/* ────── Block type options (for InsertButton menu — now sourced from BlockPalette) ────── */

const BLOCK_TYPE_OPTIONS = ALL_BLOCK_TYPES;

/* ── Block width system ── */
const WIDTH_OPTIONS = ["narrow", "medium", "wide", "full"] as const;
type BlockWidth = (typeof WIDTH_OPTIONS)[number];

const WIDTH_CLASSES: Record<BlockWidth, string> = {
  narrow: "max-w-2xl",
  medium: "max-w-4xl",
  wide: "max-w-6xl",
  full: "",
};

const WIDTH_LABELS: Record<BlockWidth, string> = {
  narrow: "\u05E6\u05E8",
  medium: "\u05D1\u05D9\u05E0\u05D5\u05E0\u05D9",
  wide: "\u05E8\u05D7\u05D1",
  full: "\u05DE\u05DC\u05D0",
};

const DEFAULT_BLOCK_WIDTH: Record<string, BlockWidth> = {
  heading: "medium",
  text: "medium",
  list: "medium",
  quote: "medium",
  callout: "medium",
  card: "medium",
  accordion: "medium",
  table: "wide",
  ctaButton: "medium",
  linkButton: "medium",
  image: "wide",
  gallery: "wide",
  stats: "wide",
  videoEmbed: "wide",
  divider: "full",
  spacer: "full",
  tabs: "wide",
  timeline: "medium",
  pricingCards: "wide",
  toggleList: "medium",
  alertBanner: "wide",
  teamCards: "wide",
  countdown: "medium",
  socialLinks: "medium",
  progressBars: "medium",
  marquee: "full",
};

function getBlockWidth(block: { type: string; meta?: Record<string, any> }): BlockWidth {
  if (block.meta?.width && WIDTH_OPTIONS.includes(block.meta.width)) {
    return block.meta.width as BlockWidth;
  }
  return DEFAULT_BLOCK_WIDTH[block.type] || "medium";
}

function cycleWidth(current: BlockWidth): BlockWidth {
  const idx = WIDTH_OPTIONS.indexOf(current);
  return WIDTH_OPTIONS[(idx + 1) % WIDTH_OPTIONS.length];
}

export function generateBlockId() {
  return `blk-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
}

export function getDefaultContent(type: string): string {
  switch (type) {
    case "heading":
      return "כותרת חדשה";
    case "text":
      return "לחצו כאן כדי לערוך את הטקסט. אפשר לכתוב כמה שורות.";
    case "list":
      return "פריט ראשון\nפריט שני\nפריט שלישי";
    case "quote":
      return "ציטוט לדוגמה — לחצו לעריכה";
    case "callout":
      return "מידע חשוב — לחצו לעריכה";
    case "card":
      return "כותרת הכרטיס";
    case "image":
      return "כיתוב התמונה";
    case "videoEmbed":
      return "";
    case "ctaButton":
      return "לחצו כאן";
    case "linkButton":
      return "קראו עוד";
    case "accordion":
      return "שאלה לדוגמה?";
    case "table":
    case "tabs":
    case "timeline":
    case "pricingCards":
    case "toggleList":
    case "teamCards":
    case "countdown":
    case "socialLinks":
    case "progressBars":
    case "marquee":
      return "";
    case "alertBanner":
      return "\u05E9\u05D9\u05DE\u05D5 \u05DC\u05D1: \u05D4\u05D5\u05D3\u05E2\u05D4 \u05D7\u05E9\u05D5\u05D1\u05D4 \u05DC\u05DC\u05E7\u05D5\u05D7\u05D5\u05EA \u05E9\u05DC\u05DB\u05DD";
    case "divider":
    case "spacer":
    case "gallery":
    case "stats":
      return "";
    default:
      return getSectionDefaultContent(type) || "";
  }
}

export function getDefaultMeta(type: string): Record<string, any> | undefined {
  switch (type) {
    case "card":
      return { emoji: "📋", description: "תיאור הכרטיס — לחצו לעריכה" };
    case "image":
      return { alt: "תיאור התמונה", imageUrl: "" };
    case "gallery":
      return {
        columns: 3,
        items: [
          { caption: "תמונה 1", imageUrl: "" },
          { caption: "תמונה 2", imageUrl: "" },
          { caption: "תמונה 3", imageUrl: "" },
        ],
      };
    case "ctaButton":
      return { link: "/contact", style: "primary" };
    case "linkButton":
      return { link: "", style: "primary" };
    case "stats":
      return {
        items: [
          { value: "100+", label: "פרויקטים" },
          { value: "15", label: "שנות ניסיון" },
          { value: "24/7", label: "תמיכה" },
        ],
      };
    case "spacer":
      return { height: 40 };
    case "videoEmbed":
      return { url: "", note: "", aspectRatio: "16:9" };
    case "accordion":
      return { answer: "התשובה כאן — לחצו לעריכה" };
    case "table":
      return {
        headerColumn: true,
        headerColor: "#1a73e8",
        rows: [
          { cells: ["כותרת", "תיאור או הסבר"] },
          { cells: ["יתרון ראשון", "תיאור היתרון הראשון — לחצו לעריכה"] },
          { cells: ["יתרון שני", "תיאור היתרון השני — לחצו לעריכה"] },
          { cells: ["יתרון שלישי", "תיאור היתרון השלישי — לחצו לעריכה"] },
        ],
      };
    case "tabs":
      return {
        tabs: [
          { id: "t1", title: "\u05D8\u05D0\u05D1 \u05E8\u05D0\u05E9\u05D5\u05DF", content: "\u05EA\u05D5\u05DB\u05DF \u05D4\u05D8\u05D0\u05D1 \u05D4\u05E8\u05D0\u05E9\u05D5\u05DF \u2014 \u05DC\u05D7\u05E6\u05D5 \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4" },
          { id: "t2", title: "\u05D8\u05D0\u05D1 \u05E9\u05E0\u05D9", content: "\u05EA\u05D5\u05DB\u05DF \u05D4\u05D8\u05D0\u05D1 \u05D4\u05E9\u05E0\u05D9 \u2014 \u05DC\u05D7\u05E6\u05D5 \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4" },
          { id: "t3", title: "\u05D8\u05D0\u05D1 \u05E9\u05DC\u05D9\u05E9\u05D9", content: "\u05EA\u05D5\u05DB\u05DF \u05D4\u05D8\u05D0\u05D1 \u05D4\u05E9\u05DC\u05D9\u05E9\u05D9 \u2014 \u05DC\u05D7\u05E6\u05D5 \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4" },
        ],
        activeTab: "t1",
      };
    case "timeline":
      return {
        items: [
          { id: "tl1", year: "2020", title: "\u05D4\u05E7\u05DE\u05EA \u05D4\u05D7\u05D1\u05E8\u05D4", description: "\u05EA\u05D9\u05D0\u05D5\u05E8 \u05E7\u05E6\u05E8 \u05E9\u05DC \u05D4\u05D0\u05D9\u05E8\u05D5\u05E2" },
          { id: "tl2", year: "2022", title: "\u05D4\u05E8\u05D7\u05D1\u05EA \u05E4\u05E2\u05D9\u05DC\u05D5\u05EA", description: "\u05EA\u05D9\u05D0\u05D5\u05E8 \u05E7\u05E6\u05E8 \u05E9\u05DC \u05D4\u05D0\u05D9\u05E8\u05D5\u05E2" },
          { id: "tl3", year: "2024", title: "\u05D4\u05E9\u05E7\u05EA \u05DE\u05D5\u05E6\u05E8 \u05D7\u05D3\u05E9", description: "\u05EA\u05D9\u05D0\u05D5\u05E8 \u05E7\u05E6\u05E8 \u05E9\u05DC \u05D4\u05D0\u05D9\u05E8\u05D5\u05E2" },
        ],
      };
    case "pricingCards":
      return {
        cards: [
          { id: "p1", title: "\u05D1\u05E1\u05D9\u05E1\u05D9", price: "\u20AA99", period: "\u05DC\u05D7\u05D5\u05D3\u05E9", features: ["\u05E4\u05D9\u05E6'\u05E8 \u05E8\u05D0\u05E9\u05D5\u05DF", "\u05E4\u05D9\u05E6'\u05E8 \u05E9\u05E0\u05D9", "\u05E4\u05D9\u05E6'\u05E8 \u05E9\u05DC\u05D9\u05E9\u05D9"], highlighted: false },
          { id: "p2", title: "\u05DE\u05E7\u05E6\u05D5\u05E2\u05D9", price: "\u20AA199", period: "\u05DC\u05D7\u05D5\u05D3\u05E9", features: ["\u05DB\u05DC \u05DE\u05D4 \u05E9\u05D1\u05D1\u05E1\u05D9\u05E1\u05D9", "\u05E4\u05D9\u05E6'\u05E8 \u05DE\u05EA\u05E7\u05D3\u05DD", "\u05EA\u05DE\u05D9\u05DB\u05D4 \u05DE\u05D5\u05E8\u05D7\u05D1\u05EA"], highlighted: true },
          { id: "p3", title: "\u05E4\u05E8\u05D9\u05DE\u05D9\u05D5\u05DD", price: "\u20AA349", period: "\u05DC\u05D7\u05D5\u05D3\u05E9", features: ["\u05DB\u05DC \u05DE\u05D4 \u05E9\u05D1\u05DE\u05E7\u05E6\u05D5\u05E2\u05D9", "\u05D0\u05D9\u05E0\u05D8\u05D2\u05E8\u05E6\u05D9\u05D5\u05EA", "\u05DE\u05E0\u05D4\u05DC \u05D7\u05E9\u05D1\u05D5\u05DF \u05D0\u05D9\u05E9\u05D9"], highlighted: false },
        ],
      };
    case "toggleList":
      return {
        items: [
          { id: "tg1", text: "\u05DE\u05E9\u05D9\u05DE\u05D4 \u05E8\u05D0\u05E9\u05D5\u05E0\u05D4 \u2014 \u05DC\u05D7\u05E6\u05D5 \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4", checked: true },
          { id: "tg2", text: "\u05DE\u05E9\u05D9\u05DE\u05D4 \u05E9\u05E0\u05D9\u05D9\u05D4 \u2014 \u05DC\u05D7\u05E6\u05D5 \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4", checked: false },
          { id: "tg3", text: "\u05DE\u05E9\u05D9\u05DE\u05D4 \u05E9\u05DC\u05D9\u05E9\u05D9\u05EA \u2014 \u05DC\u05D7\u05E6\u05D5 \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4", checked: false },
          { id: "tg4", text: "\u05DE\u05E9\u05D9\u05DE\u05D4 \u05E8\u05D1\u05D9\u05E2\u05D9\u05EA \u2014 \u05DC\u05D7\u05E6\u05D5 \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4", checked: true },
        ],
      };
    case "alertBanner":
      return { variant: "info" };
    case "teamCards":
      return {
        members: [
          { id: "tm1", name: "\u05D9\u05D5\u05E1\u05D9 \u05DB\u05D4\u05DF", role: "\u05DE\u05E0\u05DB\u05F4\u05DC", imageUrl: "", bio: "\u05EA\u05D9\u05D0\u05D5\u05E8 \u05E7\u05E6\u05E8 \u2014 \u05DC\u05D7\u05E6\u05D5 \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4" },
          { id: "tm2", name: "\u05E8\u05D5\u05E0\u05D9\u05EA \u05DC\u05D5\u05D9", role: "\u05E1\u05DE\u05E0\u05DB\u05F4\u05DC\u05D9\u05EA \u05E9\u05D9\u05D5\u05D5\u05E7", imageUrl: "", bio: "\u05EA\u05D9\u05D0\u05D5\u05E8 \u05E7\u05E6\u05E8 \u2014 \u05DC\u05D7\u05E6\u05D5 \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4" },
          { id: "tm3", name: "\u05D3\u05E0\u05D9\u05D0\u05DC \u05D0\u05D1\u05E8\u05D4\u05DD", role: "\u05DE\u05E4\u05EA\u05D7 \u05E8\u05D0\u05E9\u05D9", imageUrl: "", bio: "\u05EA\u05D9\u05D0\u05D5\u05E8 \u05E7\u05E6\u05E8 \u2014 \u05DC\u05D7\u05E6\u05D5 \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4" },
        ],
      };
    case "countdown":
      return {
        targetDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
        label: "\u05D4\u05E9\u05E7\u05D4 \u05DE\u05D9\u05D5\u05D7\u05D3\u05EA!",
        style: "cards",
      };
    case "socialLinks":
      return {
        links: [
          { id: "sl1", platform: "facebook", url: "https://facebook.com/", label: "Facebook" },
          { id: "sl2", platform: "instagram", url: "https://instagram.com/", label: "Instagram" },
          { id: "sl3", platform: "whatsapp", url: "https://wa.me/", label: "WhatsApp" },
          { id: "sl4", platform: "linkedin", url: "https://linkedin.com/", label: "LinkedIn" },
        ],
        style: "rounded",
      };
    case "progressBars":
      return {
        bars: [
          { id: "pb1", label: "\u05E2\u05D9\u05E6\u05D5\u05D1 \u05D5\u05D1\u05E8\u05E0\u05D3\u05D9\u05E0\u05D2", value: 95 },
          { id: "pb2", label: "\u05E4\u05D9\u05EA\u05D5\u05D7 \u05D0\u05EA\u05E8\u05D9\u05DD", value: 88 },
          { id: "pb3", label: "\u05E9\u05D9\u05D5\u05D5\u05E7 \u05D3\u05D9\u05D2\u05D9\u05D8\u05DC\u05D9", value: 75 },
          { id: "pb4", label: "\u05E9\u05D9\u05E8\u05D5\u05EA \u05DC\u05E7\u05D5\u05D7\u05D5\u05EA", value: 92 },
        ],
        color: "#1a73e8",
      };
    case "marquee":
      return {
        items: [
          "\u2B50 \u05DE\u05D1\u05E6\u05E2 \u05D7\u05D5\u05E8\u05E3! \u05E2\u05D3 50% \u05D4\u05E0\u05D7\u05D4",
          "\uD83D\uDE80 \u05D4\u05E9\u05E7\u05D4 \u05D7\u05D3\u05E9\u05D4 \u2014 \u05E6\u05E8\u05D5 \u05E7\u05E9\u05E8",
          "\uD83D\uDCDE \u05D7\u05D9\u05D9\u05D2\u05D5 \u05E2\u05DB\u05E9\u05D9\u05D5: 03-1234567",
          "\u2705 \u05DE\u05E9\u05DC\u05D5\u05D7 \u05D7\u05D9\u05E0\u05DD \u05DC\u05DB\u05DC \u05D4\u05D0\u05E8\u05E5",
        ],
        speed: 30,
        direction: "rtl",
        bgColor: "#1a2340",
        textColor: "#ffffff",
      };
    default:
      return getSectionDefaultMeta(type);
  }
}

/* ───────────── Hook: useBlocks ───────────── */

function useBlocks(zoneId: string) {
  const ctx = useContentEditingSafe();
  const relativePath = useRelativePath();
  const templateType = useTemplateType();
  const pageKey = relativePath === "/" ? "home" : relativePath.replace(/^\//, "");
  const storageKey = `blocks:${pageKey}:${zoneId}`;

  const isEditMode = ctx?.isEditMode ?? false;
  const getContent = ctx?.getContent ?? ((_k: string, d: string) => d);
  const saveContent = ctx?.saveContent ?? (() => {});

  let blocks: ContentBlock[] = [];
  try {
    blocks = JSON.parse(getContent(storageKey, "[]"));
    if (!Array.isArray(blocks)) blocks = [];
  } catch {
    blocks = [];
  }

  const update = useCallback(
    (newBlocks: ContentBlock[]) => {
      saveContent(storageKey, JSON.stringify(newBlocks));
    },
    [saveContent, storageKey]
  );

  const addBlock = useCallback(
    (type: string, insertAt: number, overrideContent?: string, overrideMeta?: Record<string, any>, overrideVariant?: string) => {
      const newBlock: ContentBlock = {
        id: generateBlockId(),
        type: type as ContentBlock["type"],
        content: overrideContent ?? getDefaultContent(type),
        ...(overrideVariant ? { variant: overrideVariant } : type === "callout" ? { variant: "info" } : {}),
        meta: overrideMeta ? JSON.parse(JSON.stringify(overrideMeta)) : getDefaultMeta(type),
      };
      const next = [...blocks];
      next.splice(insertAt, 0, newBlock);
      update(next);
    },
    [blocks, update]
  );

  const removeBlock = useCallback(
    (blockId: string) => update(blocks.filter((b) => b.id !== blockId)),
    [blocks, update]
  );

  const updateBlock = useCallback(
    (blockId: string, changes: Partial<ContentBlock>) =>
      update(blocks.map((b) => (b.id === blockId ? { ...b, ...changes } : b))),
    [blocks, update]
  );

  const updateContent = useCallback(
    (blockId: string, content: string) =>
      update(blocks.map((b) => (b.id === blockId ? { ...b, content } : b))),
    [blocks, update]
  );

  const updateVariant = useCallback(
    (blockId: string, variant: string) =>
      update(blocks.map((b) => (b.id === blockId ? { ...b, variant } : b))),
    [blocks, update]
  );

  const updateMeta = useCallback(
    (blockId: string, metaChanges: Record<string, any>) =>
      update(
        blocks.map((b) =>
          b.id === blockId ? { ...b, meta: { ...(b.meta || {}), ...metaChanges } } : b
        )
      ),
    [blocks, update]
  );

  const moveBlock = useCallback(
    (blockId: string, direction: "up" | "down") => {
      const idx = blocks.findIndex((b) => b.id === blockId);
      if (idx === -1) return;
      const newIdx = direction === "up" ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= blocks.length) return;
      const next = [...blocks];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      update(next);
    },
    [blocks, update]
  );

  const moveBlockToIndex = useCallback(
    (blockId: string, toIndex: number) => {
      const fromIndex = blocks.findIndex((b) => b.id === blockId);
      if (fromIndex === -1 || fromIndex === toIndex) return;
      const next = [...blocks];
      const [moved] = next.splice(fromIndex, 1);
      const adjustedIndex = toIndex > fromIndex ? toIndex - 1 : toIndex;
      next.splice(adjustedIndex, 0, moved);
      update(next);
    },
    [blocks, update]
  );

  const duplicateBlock = useCallback(
    (blockId: string) => {
      const idx = blocks.findIndex((b) => b.id === blockId);
      if (idx === -1) return;
      const original = blocks[idx];
      const clone: ContentBlock = {
        ...original,
        id: generateBlockId(),
        meta: original.meta ? JSON.parse(JSON.stringify(original.meta)) : undefined,
      };
      const next = [...blocks];
      next.splice(idx + 1, 0, clone);
      update(next);
    },
    [blocks, update]
  );

  return {
    blocks,
    isEditMode,
    addBlock,
    removeBlock,
    updateBlock,
    updateContent,
    updateVariant,
    updateMeta,
    moveBlock,
    moveBlockToIndex,
    duplicateBlock,
  };
}

/* ───────────── DropIndicator ───────────── */

function DropIndicator({
  index,
  onDropPalette,
  onDropBlock,
  isDragActive,
}: {
  index: number;
  onDropPalette: (blockType: string, index: number, savedContent?: string, savedMeta?: Record<string, any>, savedVariant?: string) => void;
  onDropBlock: (blockId: string, index: number) => void;
  isDragActive: boolean;
}) {
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: [DND_PALETTE_BLOCK, DND_ZONE_BLOCK],
      drop: (item: any, monitor) => {
        const itemType = monitor.getItemType();
        if (itemType === DND_PALETTE_BLOCK) {
          onDropPalette(item.blockType, index, item.savedContent, item.savedMeta, item.savedVariant);
        } else if (itemType === DND_ZONE_BLOCK) {
          onDropBlock(item.blockId, index);
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [index, onDropPalette, onDropBlock]
  );

  /* When a drag is active globally, show big prominent targets */
  if (isDragActive) {
    return (
      <div
        ref={drop}
        className={`transition-all duration-300 rounded-lg mx-2 ${
          isOver && canDrop
            ? "h-16 my-2 bg-zap/10 border-2 border-zap border-dashed"
            : "h-10 my-1 bg-zap/5 border-2 border-zap/30 border-dashed"
        }`}
      >
        <div className="h-full flex items-center justify-center">
          {isOver && canDrop ? (
            <div className="flex items-center gap-2">
              <div className="flex-1 h-[3px] bg-zap rounded-full w-12" />
              <span className="text-xs text-zap px-2" style={{ fontWeight: 600 }}>
                שחררו כאן
              </span>
              <div className="flex-1 h-[3px] bg-zap rounded-full w-12" />
            </div>
          ) : (
            <span className="text-[11px] text-zap/50">גררו לכאן</span>
          )}
        </div>
      </div>
    );
  }

  /* When no drag, show nothing */
  return <div ref={drop} className="h-0" />;
}

/* ───────────── InsertButton ───────────── */

function InsertButton({
  index,
  showMenu,
  onToggle,
  onAdd,
  isEmpty = false,
  emptyLabel,
}: {
  index: number;
  showMenu: boolean;
  onToggle: () => void;
  onAdd: (type: string) => void;
  isEmpty?: boolean;
  emptyLabel?: string;
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const templateType = useTemplateType();
  const filteredBlockOptions = getBlocksForType(templateType);
  const [menuSearch, setMenuSearch] = useState("");

  useEffect(() => {
    if (!showMenu) {
      setMenuSearch("");
      return;
    }
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onToggle();
      }
    }
    document.addEventListener("mousedown", handleClick);
    setTimeout(() => searchRef.current?.focus(), 100);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showMenu, onToggle]);

  const categories = [
    { key: "sections", label: "\uD83C\uDFD7\uFE0F \u05E1\u05E7\u05E9\u05E0\u05D9\u05DD" },
    { key: "content", label: "\uD83D\uDCDD \u05EA\u05D5\u05DB\u05DF" },
    { key: "media", label: "\uD83C\uDFA8 \u05DE\u05D3\u05D9\u05D4" },
    { key: "elements", label: "\uD83E\uDDE9 \u05D0\u05DC\u05DE\u05E0\u05D8\u05D9\u05DD" },
    { key: "design", label: "\u2728 \u05E2\u05D9\u05E6\u05D5\u05D1" },
  ];

  const searchFiltered = menuSearch.trim()
    ? filteredBlockOptions.filter(
        (b) =>
          b.label.toLowerCase().includes(menuSearch.toLowerCase()) ||
          b.description.toLowerCase().includes(menuSearch.toLowerCase()) ||
          b.type.toLowerCase().includes(menuSearch.toLowerCase())
      )
    : filteredBlockOptions;

  return (
    <div className={`relative ${isEmpty ? "py-6" : "py-1.5"}`} ref={menuRef}>
      <div className="flex items-center gap-3 group">
        <div className="flex-1 border-t-2 border-dashed border-gray-300 group-hover:border-zap/40 transition-colors" />
        <button
          onClick={onToggle}
          className={`shrink-0 flex items-center gap-2 rounded-full transition-all ${
            showMenu
              ? "bg-zap text-white px-4 py-1.5 shadow-md"
              : "bg-gray-100 text-gray-400 hover:bg-zap hover:text-white px-3 py-1.5 hover:shadow-md"
          }`}
        >
          <Plus className="w-4 h-4" />
          {isEmpty && (
            <span className="text-xs" style={{ fontWeight: 500 }}>
              {emptyLabel || "\u05D4\u05D5\u05E1\u05D9\u05E4\u05D5 \u05D0\u05D6\u05D5\u05E8 \u05EA\u05D5\u05DB\u05DF"}
            </span>
          )}
        </button>
        <div className="flex-1 border-t-2 border-dashed border-gray-300 group-hover:border-zap/40 transition-colors" />
      </div>

      <AnimatePresence>
        {showMenu && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 bg-white border border-gray-200 rounded-xl shadow-xl min-w-[320px] max-h-[420px] flex flex-col"
          >
            {/* Search */}
            <div className="px-3 pt-3 pb-2 border-b border-gray-100 shrink-0">
              <div className="relative">
                <Search className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  ref={searchRef}
                  type="text"
                  value={menuSearch}
                  onChange={(e) => setMenuSearch(e.target.value)}
                  placeholder={"\u05D7\u05E4\u05E9\u05D5 \u05E1\u05D5\u05D2 \u05EA\u05D5\u05DB\u05DF..."}
                  className="w-full pr-8 pl-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-gray-50 placeholder:text-gray-400 focus:outline-none focus:border-zap focus:bg-white transition-colors"
                  dir="rtl"
                />
                {menuSearch && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => { setMenuSearch(""); searchRef.current?.focus(); }}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { setMenuSearch(""); searchRef.current?.focus(); } }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <X className="w-3 h-3" />
                  </span>
                )}
              </div>
            </div>

            {/* Results */}
            <div className="overflow-y-auto p-2 flex-1">
              {searchFiltered.length === 0 ? (
                <div className="text-center py-6">
                  <Search className="w-6 h-6 text-gray-300 mx-auto mb-2" />
                  <p className="text-xs text-gray-400">{"\u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0\u05D5 \u05EA\u05D5\u05E6\u05D0\u05D5\u05EA"}</p>
                </div>
              ) : menuSearch.trim() ? (
                searchFiltered.map((opt) => (
                  <button
                    key={opt.type}
                    onClick={() => onAdd(opt.type)}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-blue-50 transition-colors text-right"
                  >
                    <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                      <opt.Icon className="w-4 h-4 text-zap" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-zap-navy" style={{ fontWeight: 600 }}>{opt.label}</p>
                      <p className="text-[11px] text-gray-400">{opt.description}</p>
                    </div>
                  </button>
                ))
              ) : (
                categories.map((cat) => {
                  const catBlocks = searchFiltered.filter((b) => b.category === cat.key);
                  if (catBlocks.length === 0) return null;
                  return (
                    <div key={cat.key}>
                      <p className="text-[10px] text-gray-400 px-3 pt-2 pb-1" style={{ fontWeight: 600 }}>
                        {cat.label}
                      </p>
                      {catBlocks.map((opt) => (
                        <button
                          key={opt.type}
                          onClick={() => onAdd(opt.type)}
                          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-blue-50 transition-colors text-right"
                        >
                          <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                            <opt.Icon className="w-4 h-4 text-zap" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-zap-navy" style={{ fontWeight: 600 }}>{opt.label}</p>
                            <p className="text-[11px] text-gray-400">{opt.description}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ───────────── Block Controls ───────────── */

function BlockControls({
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  blockType,
  variant,
  onVariantChange,
  dragRef,
  onSaveToLibrary,
  onCopyToClipboard,
  align,
  onAlignChange,
  width,
  onWidthChange,
}: {
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  blockType: string;
  variant?: string;
  onVariantChange?: (v: string) => void;
  dragRef: React.Ref<any>;
  onSaveToLibrary?: () => void;
  onCopyToClipboard?: () => void;
  align?: string;
  onAlignChange?: (a: string) => void;
  width?: BlockWidth;
  onWidthChange?: (w: BlockWidth) => void;
}) {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showVariants, setShowVariants] = useState(false);

  return (
    <div className="absolute top-1 right-1 z-[999] flex items-center gap-0.5 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-lg shadow-lg px-1 py-0.5 opacity-0 group-hover/block:opacity-100 transition-opacity">
      {/* Drag handle */}
      <div
        ref={dragRef}
        className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:text-zap hover:bg-zap-pale cursor-grab active:cursor-grabbing transition-colors"
        title="גררו לסידור מחדש"
      >
        <GripVertical className="w-3.5 h-3.5" />
      </div>

      {/* Divider */}
      <div className="w-px h-5 bg-gray-200" />

      {!isFirst && (
        <button
          onClick={onMoveUp}
          className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:text-zap hover:bg-zap-pale transition-colors"
          title="הזז למעלה"
        >
          <ChevronUp className="w-3.5 h-3.5" />
        </button>
      )}
      {!isLast && (
        <button
          onClick={onMoveDown}
          className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:text-zap hover:bg-zap-pale transition-colors"
          title="הזז למטה"
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      )}

      {/* Duplicate */}
      <button
        onClick={onDuplicate}
        className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
        title="שכפל בלוק"
      >
        <Copy className="w-3.5 h-3.5" />
      </button>

      {/* Copy to clipboard (cross-template) */}
      {onCopyToClipboard && (
        <button
          type="button"
          onClick={onCopyToClipboard}
          className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
          title="העתק ללוח (לשימוש בתבניות אחרות)"
        >
          <ClipboardCopy className="w-3.5 h-3.5" />
        </button>
      )}

      {/* Save to library */}
      {onSaveToLibrary && (
        <button
          onClick={onSaveToLibrary}
          className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
          title="שמור לספריית הסקשנים"
        >
          <Bookmark className="w-3.5 h-3.5" />
        </button>
      )}

      {/* Alignment controls */}
      {onAlignChange && !["divider", "spacer"].includes(blockType) && (
        <>
          <div className="w-px h-5 bg-gray-200" />
          {([
            { value: "right", icon: AlignRight, label: "ימין" },
            { value: "center", icon: AlignCenter, label: "מרכז" },
            { value: "left", icon: AlignLeft, label: "שמאל" },
          ] as const).map((opt) => {
            const Icon = opt.icon;
            const isActive = (align || "right") === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onAlignChange(opt.value)}
                className={`w-7 h-7 rounded-md flex items-center justify-center transition-colors ${
                  isActive
                    ? "text-indigo-600 bg-indigo-50"
                    : "text-gray-400 hover:text-zap hover:bg-zap-pale"
                }`}
                title={`יישור ${opt.label}`}
              >
                <Icon className="w-3.5 h-3.5" />
              </button>
            );
          })}
        </>
      )}

      {/* Width control */}
      {onWidthChange && !["divider", "spacer"].includes(blockType) && (
        <>
          <div className="w-px h-5 bg-gray-200" />
          <button
            type="button"
            onClick={() => onWidthChange(cycleWidth(width || "medium"))}
            className="h-7 rounded-md flex items-center gap-1 px-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-50 transition-colors"
            title={`\u05E8\u05D5\u05D7\u05D1: ${WIDTH_LABELS[width || "medium"]} \u2192 ${WIDTH_LABELS[cycleWidth(width || "medium")]}`}
          >
            <MoveHorizontal className="w-3.5 h-3.5" />
            <span className="text-[9px] font-semibold leading-none">{WIDTH_LABELS[width || "medium"]}</span>
          </button>
        </>
      )}

      {blockType === "callout" && onVariantChange && (
        <>
          <div className="w-px h-5 bg-gray-200" />
          <div className="relative">
            <button
              onClick={() => setShowVariants(!showVariants)}
              className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:text-zap hover:bg-zap-pale transition-colors"
              title="שנה סגנון"
            >
              {variant === "info" && <Info className="w-3.5 h-3.5 text-zap-glow" />}
              {variant === "warning" && <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />}
              {variant === "success" && <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />}
              {variant === "tip" && <Lightbulb className="w-3.5 h-3.5 text-purple-500" />}
              {!variant && <Info className="w-3.5 h-3.5 text-zap-glow" />}
            </button>
            {showVariants && (
              <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl p-1 flex gap-0.5 z-[1000]">
                {(["info", "warning", "success", "tip"] as const).map((v) => (
                  <button
                    key={v}
                    onClick={() => { onVariantChange(v); setShowVariants(false); }}
                    className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${
                      variant === v ? "bg-zap-pale ring-1 ring-zap" : "hover:bg-gray-100"
                    }`}
                    title={v === "info" ? "מידע" : v === "warning" ? "אזהרה" : v === "success" ? "הצלחה" : "טיפ"}
                  >
                    {v === "info" && <Info className="w-3.5 h-3.5 text-zap-glow" />}
                    {v === "warning" && <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />}
                    {v === "success" && <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />}
                    {v === "tip" && <Lightbulb className="w-3.5 h-3.5 text-purple-500" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <div className="w-px h-5 bg-gray-200" />

      {/* Delete */}
      <div className="relative">
        <button
          onClick={() => setShowConfirmDelete(true)}
          className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
          title="מחק בלוק"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
        {showConfirmDelete && (
          <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl p-3 min-w-[160px] z-[1000]">
            <p className="text-xs text-gray-600 mb-2 whitespace-nowrap">למחוק בלוק זה?</p>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  onDelete();
                  setShowConfirmDelete(false);
                }}
                className="flex-1 px-2.5 py-1.5 text-xs bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
              >
                מחק
              </button>
              <button
                onClick={() => setShowConfirmDelete(false)}
                className="flex-1 px-2.5 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors"
              >
                ביטול
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ───────────── Editable Block Content ───────────── */

const TAG_BADGE_COLORS_BLOCK: Record<string, string> = {
  h2: "bg-blue-100 text-blue-700",
  h3: "bg-cyan-100 text-cyan-700",
  p: "bg-gray-100 text-gray-600",
  span: "bg-gray-50 text-gray-500",
  div: "bg-gray-100 text-gray-500",
  blockquote: "bg-gray-100 text-gray-500",
};

const BLOCK_LEVEL_TAGS = new Set(["h2", "h3", "p", "div", "blockquote"]);

function EditableBlockContent({
  content,
  onSave,
  isEditMode,
  tag: Tag = "p",
  className = "",
  placeholder = "\u05DC\u05D7\u05E6\u05D5 \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4...",
  typo,
  onTypoChange,
  onTypoReset,
}: {
  content: string;
  onSave: (newContent: string) => void;
  isEditMode: boolean;
  tag?: "h2" | "h3" | "p" | "span" | "div" | "blockquote";
  className?: string;
  placeholder?: string;
  typo?: TypoData;
  onTypoChange?: (t: TypoData) => void;
  onTypoReset?: () => void;
}) {
  const ref = useRef<HTMLElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  const isRich = hasHtmlFormatting(content);

  // Sync DOM content from React state ONLY when not focused
  useEffect(() => {
    if (ref.current && !isFocused) {
      if (isRich) {
        ref.current.innerHTML = content;
      } else if (content.includes("\n")) {
        ref.current.innerHTML = content.replace(/\n/g, "<br>");
      } else {
        ref.current.textContent = content || "";
      }
    }
  }, [content, isFocused, isRich]);

  const handleBlur = useCallback(() => {
    if (!ref.current) { setIsFocused(false); return; }
    const newValue = extractRichValue(ref.current);
    setIsFocused(false);
    if (newValue !== content) {
      onSave(newValue);
    }
  }, [content, onSave]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Rich-text keyboard shortcuts (Ctrl+B, Ctrl+U)
      if (blockFormatKeyDown(e)) return;

      if (e.key === "Enter") {
        if (Tag === "h2" || Tag === "h3") {
          e.preventDefault();
          ref.current?.blur();
        } else if (e.shiftKey || Tag === "p" || Tag === "div" || Tag === "blockquote") {
          e.preventDefault();
          const selection = window.getSelection();
          if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            range.deleteContents();
            const br = document.createElement("br");
            range.insertNode(br);
            const textNode = document.createTextNode("\u200B");
            range.setStartAfter(br);
            range.insertNode(textNode);
            range.setStartAfter(textNode);
            range.setEndAfter(textNode);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        } else {
          e.preventDefault();
          ref.current?.blur();
        }
      }
      if (e.key === "Escape") {
        if (ref.current) ref.current.textContent = content;
        ref.current?.blur();
      }
    },
    [Tag, content]
  );

  const renderContent = (text: string) => {
    if (hasHtmlFormatting(text)) {
      return <span dangerouslySetInnerHTML={{ __html: text }} />;
    }
    if (!text.includes("\n")) return text;
    return text.split("\n").map((part, i, arr) => (
      <span key={i}>
        {part}
        {i < arr.length - 1 && <br />}
      </span>
    ));
  };

  const typoStyle: React.CSSProperties = {
    ...(typo?.fontSize ? { fontSize: typo.fontSize } : {}),
    ...(typo?.color ? { color: typo.color } : {}),
    ...(typo?.fontWeight ? { fontWeight: Number(typo.fontWeight) } : {}),
  };

  if (!isEditMode) {
    return (
      <Tag className={className} style={typoStyle}>
        {content ? renderContent(content) : <span className="text-gray-300">{placeholder}</span>}
      </Tag>
    );
  }

  const isBlock = BLOCK_LEVEL_TAGS.has(Tag);
  const Wrapper = isBlock ? "div" : "span";

  return (
    <Wrapper className={`relative ${isBlock ? "block" : "inline-block"} group/editable-block pt-5`}>
      {/* Tag badge + format bar */}
      <div className="absolute top-0 right-0 z-[20] flex items-center gap-2 opacity-80 group-hover/editable-block:opacity-100 transition-opacity">
        <span
          className={`inline-flex items-center text-xs leading-none px-2.5 py-1 rounded-full font-mono shadow-sm ${TAG_BADGE_COLORS_BLOCK[Tag] || "bg-gray-100 text-gray-500"}`}
          style={{ fontWeight: 700 }}
        >
          {Tag.toUpperCase()}
        </span>
        <BlockFormatBar
          editableEl={ref.current}
          typo={typo}
          onTypoChange={onTypoChange}
          onTypoReset={onTypoReset}
          showBullets
        />
      </div>
      <Tag
        ref={ref as any}
        className={`${className} outline-none ${
          isFocused
            ? "ring-2 ring-zap ring-offset-2 bg-zap-pale/50 rounded px-1"
            : "hover:ring-2 hover:ring-zap/40 hover:ring-offset-1 rounded cursor-text"
        }`}
        style={typoStyle}
        contentEditable
        suppressContentEditableWarning
        onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        onFocus={() => setIsFocused(true)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        dir="rtl"
        data-content-block="true"
      />
    </Wrapper>
  );
}

/* ───────────── Block Renderers ───────────── */

const CALLOUT_STYLES: Record<string, { bg: string; border: string; icon: typeof Info; iconColor: string }> = {
  info: { bg: "bg-blue-50", border: "border-blue-200", icon: Info, iconColor: "text-blue-500" },
  warning: { bg: "bg-amber-50", border: "border-amber-200", icon: AlertTriangle, iconColor: "text-amber-500" },
  success: { bg: "bg-green-50", border: "border-green-200", icon: CheckCircle2, iconColor: "text-green-500" },
  tip: { bg: "bg-purple-50", border: "border-purple-200", icon: Lightbulb, iconColor: "text-purple-500" },
};

function BlockRenderer({
  block,
  isEditMode,
  onUpdateContent,
  onUpdateMeta,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  onUpdateVariant,
  isFirst,
  isLast,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateContent: (content: string) => void;
  onUpdateMeta: (meta: Record<string, any>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onUpdateVariant: (variant: string) => void;
  isFirst: boolean;
  isLast: boolean;
}) {
  /* ─── Drag handle for reordering ─── */
  const [{ isDragging }, drag, dragPreview] = useDrag(
    () => ({
      type: DND_ZONE_BLOCK,
      item: { blockId: block.id },
      canDrag: isEditMode,
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [block.id, isEditMode]
  );

  const wrapperClass = isEditMode
    ? `relative group/block py-2 rounded-lg hover:bg-gray-50/50 transition-all ${
        isDragging ? "opacity-30" : ""
      }`
    : "py-1";

  /* ─── Typography helpers for block.meta ─── */
  const getTypo = (): TypoData => {
    const m = block.meta || {};
    return {
      fontSize: m._typo_fontSize || undefined,
      color: m._typo_color || undefined,
      fontWeight: m._typo_fontWeight || undefined,
    };
  };
  const setTypo = (t: TypoData) => {
    onUpdateMeta({
      _typo_fontSize: t.fontSize || "",
      _typo_color: t.color || "",
      _typo_fontWeight: t.fontWeight || "",
    });
  };
  const resetTypo = () => {
    onUpdateMeta({ _typo_fontSize: "", _typo_color: "", _typo_fontWeight: "" });
  };

  const renderBlockContent = () => {
    switch (block.type) {
      /* ───── Original blocks ───── */
      case "heading":
        return (
          <EditableBlockContent
            content={block.content}
            onSave={onUpdateContent}
            isEditMode={isEditMode}
            tag="h2"
            className="text-2xl md:text-3xl text-zap-navy"
            placeholder="כותרת..."
            typo={getTypo()}
            onTypoChange={setTypo}
            onTypoReset={resetTypo}
          />
        );

      case "text":
        return (
          <EditableBlockContent
            content={block.content}
            onSave={onUpdateContent}
            isEditMode={isEditMode}
            tag="p"
            className="text-gray-600 leading-relaxed text-sm md:text-base"
            placeholder="טקסט חופשי..."
            typo={getTypo()}
            onTypoChange={setTypo}
            onTypoReset={resetTypo}
          />
        );

      case "list": {
        const items = block.content.split("\n").filter((s) => s.trim());
        if (isEditMode) {
          return (
            <EditableBlockContent
              content={block.content}
              onSave={onUpdateContent}
              isEditMode={isEditMode}
              tag="div"
              className="text-gray-600 text-sm md:text-base leading-relaxed"
              placeholder="פריט בכל שורה..."
              typo={getTypo()}
              onTypoChange={setTypo}
              onTypoReset={resetTypo}
            />
          );
        }
        return (
          <ul className="space-y-2 text-gray-600 text-sm md:text-base">
            {items.map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-zap mt-1 shrink-0">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        );
      }

      case "quote":
        return (
          <blockquote className="border-r-4 border-zap pr-6 py-3">
            <EditableBlockContent
              content={block.content}
              onSave={onUpdateContent}
              isEditMode={isEditMode}
              tag="p"
              className="text-gray-600 italic text-sm md:text-base leading-relaxed"
              placeholder="ציטוט..."
              typo={getTypo()}
              onTypoChange={setTypo}
              onTypoReset={resetTypo}
            />
          </blockquote>
        );

      case "callout": {
        const variant = block.variant || "info";
        const style = CALLOUT_STYLES[variant] || CALLOUT_STYLES.info;
        const IconComp = style.icon;
        return (
          <div className={`flex gap-3 items-start p-5 rounded-xl border ${style.bg} ${style.border}`}>
            <IconComp className={`w-5 h-5 ${style.iconColor} shrink-0 mt-0.5`} />
            <EditableBlockContent
              content={block.content}
              onSave={onUpdateContent}
              isEditMode={isEditMode}
              tag="p"
              className="text-gray-700 text-sm md:text-base leading-relaxed flex-1"
              placeholder="תוכן ההדגשה..."
              typo={getTypo()}
              onTypoChange={setTypo}
              onTypoReset={resetTypo}
            />
          </div>
        );
      }

      case "divider":
        return <hr className="border-t border-gray-200 my-4" />;

      /* ───── New blocks ───── */

      case "card":
        return <CardBlock block={block} isEditMode={isEditMode} onUpdateContent={onUpdateContent} onUpdateMeta={onUpdateMeta} />;

      case "image":
        return <ImageBlock block={block} isEditMode={isEditMode} onUpdateContent={onUpdateContent} onUpdateMeta={onUpdateMeta} />;

      case "gallery":
        return <GalleryBlock block={block} isEditMode={isEditMode} onUpdateMeta={onUpdateMeta} />;

      case "videoEmbed":
        return <VideoEmbedBlock block={block} isEditMode={isEditMode} onUpdateMeta={onUpdateMeta} />;

      case "ctaButton":
        return <CTAButtonBlock block={block} isEditMode={isEditMode} onUpdateContent={onUpdateContent} onUpdateMeta={onUpdateMeta} />;

      case "linkButton":
        return <LinkButtonBlock block={block} isEditMode={isEditMode} onUpdateContent={onUpdateContent} onUpdateMeta={onUpdateMeta} />;

      case "stats":
        return <StatsBlock block={block} isEditMode={isEditMode} onUpdateMeta={onUpdateMeta} />;

      case "spacer":
        return <SpacerBlock block={block} isEditMode={isEditMode} onUpdateMeta={onUpdateMeta} />;

      case "accordion":
        return <AccordionBlock block={block} isEditMode={isEditMode} onUpdateContent={onUpdateContent} onUpdateMeta={onUpdateMeta} />;

      case "table":
        return <TableBlock block={block} isEditMode={isEditMode} onUpdateMeta={onUpdateMeta} />;

      case "tabs":
        return <TabsBlock block={block} isEditMode={isEditMode} onUpdateMeta={onUpdateMeta} />;

      case "timeline":
        return <TimelineBlock block={block} isEditMode={isEditMode} onUpdateMeta={onUpdateMeta} />;

      case "pricingCards":
        return <PricingCardsBlock block={block} isEditMode={isEditMode} onUpdateMeta={onUpdateMeta} />;

      case "toggleList":
        return <ToggleListBlock block={block} isEditMode={isEditMode} onUpdateMeta={onUpdateMeta} />;

      case "alertBanner":
        return <AlertBannerBlock block={block} isEditMode={isEditMode} onUpdateContent={onUpdateContent} onUpdateMeta={onUpdateMeta} />;

      case "teamCards":
        return <TeamCardsBlock block={block} isEditMode={isEditMode} onUpdateMeta={onUpdateMeta} />;

      case "countdown":
        return <CountdownBlock block={block} isEditMode={isEditMode} onUpdateMeta={onUpdateMeta} />;

      case "socialLinks":
        return <SocialLinksBlock block={block} isEditMode={isEditMode} onUpdateMeta={onUpdateMeta} />;

      case "progressBars":
        return <ProgressBarsBlock block={block} isEditMode={isEditMode} onUpdateMeta={onUpdateMeta} />;

      case "marquee":
        return <MarqueeBlock block={block} isEditMode={isEditMode} onUpdateMeta={onUpdateMeta} />;

      default:
        /* ── Section blocks (composite sections) ── */
        if (isSectionType(block.type)) {
          return (
            <div className="space-y-2">
              <DropSlot id={`sec-${block.id}-top`} />
              <SectionDropSlotProvider value={(slotId: string) => <DropSlot id={slotId} />}>
                <SectionBlockRenderer
                  block={block}
                  isEditMode={isEditMode}
                  onUpdateContent={onUpdateContent}
                  onUpdateMeta={onUpdateMeta}
                />
              </SectionDropSlotProvider>
              <DropSlot id={`sec-${block.id}-bottom`} />
            </div>
          );
        }
        return null;
    }
  };

  const blockLabel = BLOCK_TYPE_OPTIONS.find((o) => o.type === block.type)?.label;

  /* ─── Save to library handler ─── */
  const { refresh: refreshSavedSections } = useSavedSections();

  const handleSaveToLibrary = useCallback(async () => {
    const builtInLabel = BLOCK_TYPE_OPTIONS.find((o) => o.type === block.type)?.label || block.type;
    const name = prompt("\u05E9\u05DD \u05DC\u05E1\u05E7\u05E9\u05DF \u05D4\u05E9\u05DE\u05D5\u05E8:", block.content?.slice(0, 40) || builtInLabel);
    if (!name) return;
    const desc = prompt("\u05EA\u05D9\u05D0\u05D5\u05E8 \u05E7\u05E6\u05E8 (\u05D0\u05D5\u05E4\u05E6\u05D9\u05D5\u05E0\u05DC\u05D9):", builtInLabel) || "";
    try {
      await saveSectionToLib({
        name,
        description: desc,
        blockType: block.type,
        content: block.content,
        meta: block.meta ? JSON.parse(JSON.stringify(block.meta)) : undefined,
        variant: block.variant,
      });
      refreshSavedSections();
      alert("\u05D4\u05E1\u05E7\u05E9\u05DF \"" + name + "\" \u05E0\u05E9\u05DE\u05E8 \u05D1\u05D4\u05E6\u05DC\u05D7\u05D4 \u05DC\u05E1\u05E4\u05E8\u05D9\u05D9\u05D4!");
    } catch (err) {
      console.error("[SaveToLibrary] error:", err);
      alert("\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05E9\u05DE\u05D9\u05E8\u05EA \u05D4\u05E1\u05E7\u05E9\u05DF");
    }
  }, [block, refreshSavedSections]);

  /* ─── Copy to clipboard handler ─── */
  const handleCopyToClipboard = useCallback(() => {
    copyBlockToClipboard(block);
    notifyClipboardChange();
    // Quick visual feedback
    const el = document.createElement("div");
    el.textContent = "\u05D4\u05D5\u05E2\u05EA\u05E7!";
    el.style.cssText = "position:fixed;top:20px;left:50%;transform:translateX(-50%);z-index:9999;background:#059669;color:#fff;padding:6px 16px;border-radius:8px;font-size:13px;font-weight:600;box-shadow:0 4px 12px rgba(0,0,0,0.15);pointer-events:none;";
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 1200);
  }, [block]);

  return (
    <motion.div
      ref={dragPreview}
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
      className={wrapperClass}
    >
      {isEditMode && (
        <BlockControls
          onDelete={onDelete}
          onDuplicate={onDuplicate}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          isFirst={isFirst}
          isLast={isLast}
          blockType={block.type}
          variant={block.variant}
          onVariantChange={onUpdateVariant}
          dragRef={drag}
          onSaveToLibrary={handleSaveToLibrary}
          onCopyToClipboard={handleCopyToClipboard}
          align={block.meta?.align}
          onAlignChange={(a) => onUpdateMeta({ align: a })}
          width={getBlockWidth(block)}
          onWidthChange={(w) => onUpdateMeta({ width: w })}
        />
      )}

      {/* Block type badge + alignment + width indicator */}
      {isEditMode && block.type !== "divider" && block.type !== "spacer" && (
        <div className="absolute top-1 left-2 opacity-0 group-hover/block:opacity-100 transition-opacity flex items-center gap-1">
          <span className="text-[10px] text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">
            {blockLabel}
          </span>
          {block.meta?.align && block.meta.align !== "right" && (
            <span className="text-[10px] text-indigo-400 bg-indigo-50 rounded px-1.5 py-0.5">
              {block.meta.align === "center" ? "\u05DE\u05E8\u05DB\u05D6" : "\u05E9\u05DE\u05D0\u05DC"}
            </span>
          )}
          {(() => {
            const bw = getBlockWidth(block);
            const isCustom = !!block.meta?.width;
            return (
              <span className={`text-[10px] rounded px-1.5 py-0.5 ${isCustom ? "text-teal-600 bg-teal-50" : "text-gray-400 bg-gray-50"}`}>
                {"\u2194"} {WIDTH_LABELS[bw]}
              </span>
            );
          })()}
        </div>
      )}

      {(() => {
        const align = block.meta?.align || "right";
        const skipCompletely = ["divider", "spacer"].includes(block.type);
        const isSection = isSectionType(block.type);

        /* ── Width wrapper ── */
        const bw = getBlockWidth(block);
        const widthClass = (isSection || skipCompletely) ? "" : WIDTH_CLASSES[bw];

        let inner: React.ReactNode;
        if (skipCompletely || align === "right") {
          inner = renderBlockContent();
        } else if (isSection) {
          const textAlignClass = align === "center" ? "text-center" : "text-left";
          inner = (
            <div className={textAlignClass}>
              {renderBlockContent()}
            </div>
          );
        } else {
          const alignClass = align === "center"
            ? "flex flex-col items-center text-center"
            : "flex flex-col items-end text-left";
          inner = (
            <div className={alignClass}>
              {renderBlockContent()}
            </div>
          );
        }

        if (widthClass) {
          return (
            <div className={`${widthClass} mx-auto ${isEditMode ? "border border-dashed border-transparent hover:border-teal-200 rounded-lg" : ""}`}>
              {inner}
            </div>
          );
        }
        return inner;
      })()}
    </motion.div>
  );
}

/* ═══════════════ New Block Type Components ═══════════════ */

/* ── Card ── */
function CardBlock({
  block,
  isEditMode,
  onUpdateContent,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateContent: (c: string) => void;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const emoji = block.meta?.emoji || "📋";
  const description = block.meta?.description || "";

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        <div className="shrink-0">
          {isEditMode ? (
            <IconEmojiField
              value={emoji}
              onChange={(val) => onUpdateMeta({ emoji: val })}
            />
          ) : (
            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-2xl">
              {emoji}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <EditableBlockContent
            content={block.content}
            onSave={onUpdateContent}
            isEditMode={isEditMode}
            tag="h3"
            className="text-zap-navy text-lg mb-1"
            placeholder="כותרת הכרטיס..."
          />
          <EditableBlockContent
            content={description}
            onSave={(val) => onUpdateMeta({ description: val })}
            isEditMode={isEditMode}
            tag="p"
            className="text-gray-600 text-sm leading-relaxed"
            placeholder="תיאור הכרטיס..."
          />
        </div>
      </div>
    </div>
  );
}

/* ── Image ── */
function ImageBlock({
  block,
  isEditMode,
  onUpdateContent,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateContent: (c: string) => void;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const meta = block.meta || {};
  const imageUrl = meta.imageUrl || "";

  return (
    <div className="rounded-xl overflow-hidden">
      <EditableImage
        src={imageUrl}
        onChangeSrc={(url) => onUpdateMeta({ imageUrl: url })}
        isEditMode={isEditMode}
        alt={meta.alt || block.content || ""}
        className="w-full h-64 md:h-80 object-cover rounded-xl"
        placeholderClassName="h-64 md:h-80"
      />
      {(block.content || isEditMode) && (
        <div className="py-2">
          <EditableBlockContent
            content={block.content}
            onSave={onUpdateContent}
            isEditMode={isEditMode}
            tag="p"
            className="text-gray-500 text-xs text-center italic"
            placeholder="כיתוב התמונה..."
          />
        </div>
      )}
    </div>
  );
}

/* ── Gallery ── */
function GalleryBlock({
  block,
  isEditMode,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const meta = block.meta || { columns: 3, items: [] };
  const columns = meta.columns || 3;
  const items: { caption: string; imageUrl?: string }[] = meta.items || [];

  const addImage = () => {
    onUpdateMeta({ items: [...items, { caption: `תמונה ${items.length + 1}`, imageUrl: "" }] });
  };

  const removeImage = (idx: number) => {
    onUpdateMeta({ items: items.filter((_, i) => i !== idx) });
  };

  const updateItemImage = (idx: number, url: string) => {
    const updated = items.map((item, i) => i === idx ? { ...item, imageUrl: url } : item);
    onUpdateMeta({ items: updated });
  };

  return (
    <div>
      <div className={`grid gap-3`} style={{ gridTemplateColumns: `repeat(${Math.min(columns, 4)}, 1fr)` }}>
        {items.map((item, i) => (
          <div key={i} className="relative group/gallery-item aspect-square rounded-lg overflow-hidden">
            <EditableImage
              src={item.imageUrl}
              onChangeSrc={(url) => updateItemImage(i, url)}
              isEditMode={isEditMode}
              alt={item.caption}
              className="w-full h-full object-cover"
              placeholderClassName="aspect-square"
              placeholderText={item.caption}
            />
            {isEditMode && (
              <button
                onClick={() => removeImage(i)}
                className="absolute top-1 left-1 z-10 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/gallery-item:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            )}
            {item.caption && item.imageUrl && (
              <div className="absolute bottom-0 inset-x-0 bg-black/40 backdrop-blur-sm px-2 py-1">
                <p className="text-white text-[10px] truncate">{item.caption}</p>
              </div>
            )}
          </div>
        ))}
        {isEditMode && (
          <button
            onClick={addImage}
            className="aspect-square border-2 border-dashed border-blue-200 rounded-lg flex items-center justify-center text-blue-400 hover:border-zap hover:text-zap hover:bg-zap-pale/30 transition-all"
          >
            <Plus className="w-6 h-6" />
          </button>
        )}
      </div>
      {isEditMode && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-[11px] text-gray-400">עמודות:</span>
          {[2, 3, 4].map((n) => (
            <button
              key={n}
              onClick={() => onUpdateMeta({ columns: n })}
              className={`text-[11px] px-2 py-0.5 rounded ${
                columns === n ? "bg-zap text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Video Embed ── */

/** Extract YouTube video ID from various URL formats */
function extractYouTubeId(url: string): string | null {
  if (!url) return null;
  // youtube.com/watch?v=ID
  const match1 = url.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
  if (match1) return match1[1];
  // youtu.be/ID
  const match2 = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
  if (match2) return match2[1];
  // youtube.com/embed/ID
  const match3 = url.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/);
  if (match3) return match3[1];
  // youtube.com/shorts/ID
  const match4 = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/);
  if (match4) return match4[1];
  return null;
}

/** Extract Vimeo video ID */
function extractVimeoId(url: string): string | null {
  if (!url) return null;
  const match = url.match(/vimeo\.com\/(\d+)/);
  return match ? match[1] : null;
}

function VideoEmbedBlock({
  block,
  isEditMode,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const meta = block.meta || { url: "", note: "", aspectRatio: "16:9" };
  const url = (meta.url || "").trim();
  const note = meta.note || "";
  const aspectRatio = meta.aspectRatio || "16:9";

  const youtubeId = extractYouTubeId(url);
  const vimeoId = extractVimeoId(url);
  const hasValidUrl = !!(youtubeId || vimeoId);
  const thumbnailUrl = youtubeId
    ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`
    : null;

  const platform = youtubeId ? "YouTube" : vimeoId ? "Vimeo" : null;
  const platformColor = platform === "YouTube" ? "#FF0000" : "#1AB7EA";

  const paddingBottom = aspectRatio === "9:16" ? "177.78%" : aspectRatio === "1:1" ? "100%" : "56.25%";

  /* ── View mode ── */
  if (!isEditMode) {
    if (!hasValidUrl) return null;
    return (
      <div className="max-w-lg rounded-xl overflow-hidden border border-gray-200 shadow-sm">
        {/* Thumbnail preview */}
        <div className="relative" style={{ paddingBottom }}>
          {thumbnailUrl ? (
            <img src={thumbnailUrl} alt="YouTube video thumbnail" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
              <Video className="w-12 h-12 text-white/30" />
            </div>
          )}
          {/* Play overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 rounded-full flex items-center justify-center shadow-xl" style={{ backgroundColor: platformColor }}>
              <Play className="w-5 h-5 text-white fill-white" style={{ marginRight: -1 }} />
            </div>
          </div>
          {/* Platform badge */}
          <div className="absolute top-2 right-2">
            <span className="text-[10px] text-white px-2 py-0.5 rounded-full shadow-lg" style={{ fontWeight: 600, backgroundColor: platformColor }}>{platform}</span>
          </div>
          {/* Dev instruction banner */}
          <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent px-3 py-2">
            <div className="flex items-center gap-1.5 text-white/90">
              <Video className="w-3.5 h-3.5 shrink-0" />
              <span className="text-[11px]" style={{ fontWeight: 500 }}>{"Embed \u05E1\u05E8\u05D8\u05D5\u05DF \u05DE\u05D5\u05D8\u05DE\u05E2 \u05D1\u05DE\u05E7\u05D5\u05DD \u05D4\u05D6\u05D4"}</span>
            </div>
          </div>
        </div>
        {/* URL display */}
        <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 flex items-center gap-2 text-xs text-gray-500" dir="ltr">
          <ExternalLink className="w-3.5 h-3.5 shrink-0" />
          <span className="truncate">{url}</span>
        </div>
        {/* Dev note */}
        {note && (
          <div className="px-3 py-2 bg-amber-50 border-t border-amber-100 flex items-start gap-2" dir="rtl">
            <MessageSquare className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700 leading-relaxed">{note}</p>
          </div>
        )}
      </div>
    );
  }

  /* ── Edit mode ── */
  return (
    <div className="max-w-lg rounded-xl overflow-hidden border-2 border-dashed border-indigo-300 bg-indigo-50/30">
      {/* Preview area */}
      <div className="relative" style={{ paddingBottom: hasValidUrl ? paddingBottom : undefined, height: hasValidUrl ? undefined : "140px" }}>
        {hasValidUrl && thumbnailUrl ? (
          <>
            <img src={thumbnailUrl} alt="YouTube video thumbnail" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
              <div className="w-12 h-12 rounded-full flex items-center justify-center shadow-xl" style={{ backgroundColor: platformColor }}>
                <Play className="w-5 h-5 text-white fill-white" style={{ marginRight: -1 }} />
              </div>
            </div>
            <div className="absolute top-2 right-2">
              <span className="text-[10px] text-white px-2 py-0.5 rounded-full shadow-lg" style={{ fontWeight: 600, backgroundColor: platformColor }}>{platform}</span>
            </div>
          </>
        ) : hasValidUrl && vimeoId ? (
          <div className="absolute inset-0 bg-gradient-to-br from-[#1AB7EA]/20 to-[#1AB7EA]/5 flex items-center justify-center">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2" style={{ backgroundColor: "#1AB7EA" }}>
                <Play className="w-5 h-5 text-white fill-white" style={{ marginRight: -1 }} />
              </div>
              <p className="text-sm text-[#1AB7EA]" style={{ fontWeight: 600 }}>Vimeo</p>
              <p className="text-[11px] text-gray-400 mt-0.5" dir="ltr">ID: {vimeoId}</p>
            </div>
          </div>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-gray-400">
            <div className="w-11 h-11 rounded-full bg-indigo-100 flex items-center justify-center">
              <Video className="w-5 h-5 text-indigo-400" />
            </div>
            <p className="text-xs text-indigo-500" style={{ fontWeight: 500 }}>{"\u05D4\u05D3\u05D1\u05D9\u05E7\u05D5 \u05E7\u05D9\u05E9\u05D5\u05E8 \u05DC\u05E1\u05E8\u05D8\u05D5\u05DF"}</p>
            <p className="text-[10px] text-gray-400">YouTube / Vimeo</p>
          </div>
        )}
      </div>

      {/* Editing controls */}
      <div className="p-3 space-y-2.5 bg-white border-t border-indigo-200/50" dir="rtl">
        {/* URL field */}
        <div>
          <label className="text-[11px] text-gray-500 mb-1 flex items-center gap-1.5" style={{ fontWeight: 600 }}>
            <Video className="w-3.5 h-3.5" />{"\u05E7\u05D9\u05E9\u05D5\u05E8 \u05DC\u05E1\u05E8\u05D8\u05D5\u05DF"}
          </label>
          <input
            type="text"
            value={url}
            onChange={(e) => onUpdateMeta({ url: e.target.value })}
            placeholder="https://www.youtube.com/watch?v=..."
            className="w-full text-xs px-2.5 py-1.5 border border-gray-200 rounded-lg bg-gray-50 placeholder:text-gray-300 focus:outline-none focus:border-indigo-400 focus:bg-white transition-colors"
            dir="ltr"
          />
          {url && !hasValidUrl && (
            <p className="text-[10px] text-red-400 mt-0.5">{"\u05DC\u05D0 \u05D6\u05D5\u05D4\u05D4 \u05DB\u05E7\u05D9\u05E9\u05D5\u05E8 YouTube \u05D0\u05D5 Vimeo \u05EA\u05E7\u05D9\u05DF"}</p>
          )}
          {hasValidUrl && (
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-[10px] text-emerald-500 flex items-center gap-1">{"✓ "}{platform}{" \u2014 \u05D6\u05D5\u05D4\u05D4 \u05D1\u05E1\u05D3\u05E8!"}</p>
              <a href={url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-indigo-400 hover:text-indigo-600 flex items-center gap-0.5">
                <ExternalLink className="w-2.5 h-2.5" />{"\u05E4\u05EA\u05D7 \u05D1\u05D8\u05D0\u05D1 \u05D7\u05D3\u05E9"}
              </a>
            </div>
          )}
        </div>

        {/* Aspect ratio + Note row */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="shrink-0">
            <span className="text-[10px] text-gray-400 block mb-1" style={{ fontWeight: 600 }}>{"\u05D9\u05D7\u05E1 \u05EA\u05DE\u05D5\u05E0\u05D4"}</span>
            <div className="flex gap-1">
              {[
                { value: "16:9", label: "16:9", desc: "\u05E8\u05D7\u05D1" },
                { value: "9:16", label: "9:16", desc: "\u05D0\u05E0\u05DB\u05D9" },
                { value: "1:1", label: "1:1", desc: "\u05E8\u05D9\u05D1\u05D5\u05E2" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => onUpdateMeta({ aspectRatio: opt.value })}
                  className={`btnReset text-[10px] px-2 py-1 rounded transition-colors ${aspectRatio === opt.value ? "text-white shadow-sm" : "text-gray-500 hover:bg-gray-100"}`}
                  style={aspectRatio === opt.value ? { backgroundColor: "#6366f1", fontWeight: 600 } : { fontWeight: 500, backgroundColor: "transparent" }}
                >{opt.label} ({opt.desc})</button>
              ))}
            </div>
          </div>
          <div className="flex-1 min-w-[160px]">
            <span className="text-[10px] text-gray-400 flex items-center gap-1 mb-1" style={{ fontWeight: 600 }}>
              <MessageSquare className="w-2.5 h-2.5" />{"\u05D4\u05E2\u05E8\u05D4 \u05DC\u05DE\u05E4\u05EA\u05D7"}<span className="text-gray-300" style={{ fontWeight: 400 }}>({"\u05D0\u05D5\u05E4\u05E6\u05D9\u05D5\u05E0\u05DC\u05D9"})</span>
            </span>
            <input
              type="text"
              value={note}
              onChange={(e) => onUpdateMeta({ note: e.target.value })}
              placeholder={"\u05DC\u05DE\u05E9\u05DC: \u05E1\u05E8\u05D8\u05D5\u05DF \u05DE\u05D5\u05D8\u05DE\u05E2 \u05D1\u05E8\u05D5\u05D7\u05D1 \u05DE\u05DC\u05D0, \u05DE\u05EA\u05D7\u05EA \u05D4Hero..."}
              className="w-full text-[11px] px-2.5 py-1.5 border border-gray-200 rounded-lg bg-white placeholder:text-gray-300 focus:outline-none focus:border-amber-400 transition-colors"
              dir="rtl"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── CTA Button ── */
function CTAButtonBlock({
  block,
  isEditMode,
  onUpdateContent,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateContent: (c: string) => void;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const meta = block.meta || { link: "/contact", style: "primary" };
  const style = meta.style || "primary";

  const styleClasses: Record<string, string> = {
    primary: "bg-zap text-white hover:bg-zap-dark",
    outline: "border-2 border-zap text-zap hover:bg-zap hover:text-white",
    whatsapp: "bg-[#25D366] text-white hover:bg-[#1fb955]",
    dark: "bg-zap-navy text-white hover:bg-zap-dark",
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <div className={`inline-flex items-center px-8 py-3 rounded-lg text-sm transition-colors ${styleClasses[style] || styleClasses.primary}`} style={{ fontWeight: 600 }}>
        {isEditMode ? (
          <input
            type="text"
            value={block.content}
            onChange={(e) => onUpdateContent(e.target.value)}
            className="bg-transparent outline-none text-center text-inherit min-w-[120px]"
            dir="rtl"
            placeholder="טקסט הכפתור..."
          />
        ) : (() => {
          const href = meta.link || "/contact";
          const isExternal = href.startsWith("http://") || href.startsWith("https://");
          const label = block.content || "לחצו כאן";
          if (isExternal) {
            return <a href={href} target="_blank" rel="noopener noreferrer">{label}</a>;
          }
          return <Link to={href}>{label}</Link>;
        })()}
      </div>
      {isEditMode && (
        <div className="flex items-center gap-3 flex-wrap justify-center">
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-gray-400">קישור:</span>
            <input
              type="text"
              value={meta.link || ""}
              onChange={(e) => onUpdateMeta({ link: e.target.value })}
              className="text-[11px] px-2 py-0.5 border border-gray-200 rounded w-32"
              dir="ltr"
              placeholder="/contact"
            />
            <PageLinkPicker
              value={meta.link || ""}
              onChange={(path) => onUpdateMeta({ link: path })}
              compact
              placeholder="בחר עמוד"
            />
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-gray-400">סגנון:</span>
            {(["primary", "outline", "whatsapp", "dark"] as const).map((s) => (
              <button
                key={s}
                onClick={() => onUpdateMeta({ style: s })}
                className={`text-[10px] px-2 py-0.5 rounded ${
                  style === s ? "bg-zap text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                }`}
              >
                {s === "primary" ? "ראשי" : s === "outline" ? "מסגרת" : s === "whatsapp" ? "ווטסאפ" : "כהה"}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Link Button ── */
function LinkButtonBlock({
  block,
  isEditMode,
  onUpdateContent,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateContent: (c: string) => void;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const meta = block.meta || { link: "/contact", style: "primary" };
  const style = meta.style || "primary";

  const styleClasses: Record<string, string> = {
    primary: "bg-zap text-white hover:bg-zap-dark",
    outline: "border-2 border-zap text-zap hover:bg-zap hover:text-white",
    whatsapp: "bg-[#25D366] text-white hover:bg-[#1fb955]",
    dark: "bg-zap-navy text-white hover:bg-zap-dark",
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <div className={`inline-flex items-center px-8 py-3 rounded-lg text-sm transition-colors ${styleClasses[style] || styleClasses.primary}`} style={{ fontWeight: 600 }}>
        {isEditMode ? (
          <input
            type="text"
            value={block.content}
            onChange={(e) => onUpdateContent(e.target.value)}
            className="bg-transparent outline-none text-center text-inherit min-w-[120px]"
            dir="rtl"
            placeholder="טקסט הכפתור..."
          />
        ) : (() => {
          const href = meta.link || "/contact";
          const isExternal = href.startsWith("http://") || href.startsWith("https://");
          const label = block.content || "קראו עוד";
          if (isExternal) {
            return <a href={href} target="_blank" rel="noopener noreferrer">{label}</a>;
          }
          return <Link to={href}>{label}</Link>;
        })()}
      </div>
      {isEditMode && (
        <div className="flex items-center gap-3 flex-wrap justify-center">
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-gray-400">קישור:</span>
            <input
              type="text"
              value={meta.link || ""}
              onChange={(e) => onUpdateMeta({ link: e.target.value })}
              className="text-[11px] px-2 py-0.5 border border-gray-200 rounded w-32"
              dir="ltr"
              placeholder="/contact"
            />
            <PageLinkPicker
              value={meta.link || ""}
              onChange={(path) => onUpdateMeta({ link: path })}
              compact
              placeholder="בחר עמוד"
            />
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-gray-400">סגנון:</span>
            {(["primary", "outline", "whatsapp", "dark"] as const).map((s) => (
              <button
                key={s}
                onClick={() => onUpdateMeta({ style: s })}
                className={`text-[10px] px-2 py-0.5 rounded ${
                  style === s ? "bg-zap text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                }`}
              >
                {s === "primary" ? "ראשי" : s === "outline" ? "מסגרת" : s === "whatsapp" ? "ווטסאפ" : "כהה"}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Stats ── */
function StatsBlock({
  block,
  isEditMode,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const items: { value: string; label: string }[] = block.meta?.items || [];

  const updateItem = (idx: number, field: "value" | "label", val: string) => {
    const next = items.map((item, i) => (i === idx ? { ...item, [field]: val } : item));
    onUpdateMeta({ items: next });
  };

  const addItem = () => {
    onUpdateMeta({ items: [...items, { value: "0", label: "תווית" }] });
  };

  const removeItem = (idx: number) => {
    onUpdateMeta({ items: items.filter((_, i) => i !== idx) });
  };

  return (
    <div className="bg-gradient-to-l from-zap-navy to-zap rounded-xl p-8">
      <div className={`grid gap-6`} style={{ gridTemplateColumns: `repeat(${Math.min(items.length || 1, 4)}, 1fr)` }}>
        {items.map((item, i) => (
          <div key={i} className="text-center relative group/stat">
            {isEditMode ? (
              <>
                <input
                  type="text"
                  value={item.value}
                  onChange={(e) => updateItem(i, "value", e.target.value)}
                  className="block w-full text-center text-3xl md:text-4xl text-white bg-transparent outline-none mb-1"
                  style={{ fontWeight: 700 }}
                  dir="rtl"
                />
                <input
                  type="text"
                  value={item.label}
                  onChange={(e) => updateItem(i, "label", e.target.value)}
                  className="block w-full text-center text-sm text-white/70 bg-transparent outline-none"
                  dir="rtl"
                />
                <button
                  onClick={() => removeItem(i)}
                  className="absolute -top-1 -left-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/stat:opacity-100 transition-opacity text-xs"
                >
                  <X className="w-3 h-3" />
                </button>
              </>
            ) : (
              <>
                <p className="text-3xl md:text-4xl text-white mb-1" style={{ fontWeight: 700 }}>{item.value}</p>
                <p className="text-sm text-white/70">{item.label}</p>
              </>
            )}
          </div>
        ))}
        {isEditMode && (
          <button
            onClick={addItem}
            className="flex flex-col items-center justify-center text-white/40 hover:text-white/70 transition-colors min-h-[80px]"
          >
            <Plus className="w-6 h-6" />
            <span className="text-xs mt-1">הוסף</span>
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Spacer ── */
function SpacerBlock({
  block,
  isEditMode,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const height = block.meta?.height || 40;

  if (!isEditMode) {
    return <div style={{ height: `${height}px` }} />;
  }

  return (
    <div
      className="border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center bg-gray-50/50 relative"
      style={{ height: `${Math.max(height, 30)}px` }}
    >
      <div className="flex items-center gap-3">
        <span className="text-[11px] text-gray-400">גובה:</span>
        <input
          type="range"
          min={8}
          max={200}
          value={height}
          onChange={(e) => onUpdateMeta({ height: parseInt(e.target.value) })}
          className="w-32 accent-zap"
        />
        <span className="text-[11px] text-gray-500" style={{ fontWeight: 500 }}>{height}px</span>
      </div>
    </div>
  );
}

/* ── Table ── */
function TableBlock({
  block,
  isEditMode,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const meta = block.meta || {};
  const rows: { cells: string[] }[] = meta.rows || [];
  const headerColumn: boolean = meta.headerColumn ?? true;
  const headerColor: string = meta.headerColor || "#1a73e8";

  const updateCell = (rowIdx: number, colIdx: number, value: string) => {
    const next = rows.map((row, ri) =>
      ri === rowIdx
        ? { cells: row.cells.map((c: string, ci: number) => (ci === colIdx ? value : c)) }
        : row
    );
    onUpdateMeta({ rows: next });
  };

  const addRow = () => {
    const colCount = rows.length > 0 ? rows[0].cells.length : 2;
    const newCells = Array.from({ length: colCount }, (_, i) =>
      i === 0 ? "\u05DB\u05D5\u05EA\u05E8\u05EA" : "\u05EA\u05D9\u05D0\u05D5\u05E8..."
    );
    onUpdateMeta({ rows: [...rows, { cells: newCells }] });
  };

  const removeRow = (idx: number) => {
    if (rows.length <= 1) return;
    onUpdateMeta({ rows: rows.filter((_: any, i: number) => i !== idx) });
  };

  const addColumn = () => {
    onUpdateMeta({
      rows: rows.map((row: { cells: string[] }) => ({
        cells: [...row.cells, "\u05E2\u05DE\u05D5\u05D3\u05D4"],
      })),
    });
  };

  const removeColumn = (colIdx: number) => {
    if (rows.length > 0 && rows[0].cells.length <= 2) return;
    onUpdateMeta({
      rows: rows.map((row: { cells: string[] }) => ({
        cells: row.cells.filter((_: string, ci: number) => ci !== colIdx),
      })),
    });
  };

  const colCount = rows.length > 0 ? rows[0].cells.length : 2;

  return (
    <div className="relative group/table">
      {/* Edit toolbar */}
      {isEditMode && (
        <div
          className="flex items-center gap-2 mb-2 flex-wrap"
          dir="rtl"
        >
          <button
            type="button"
            onClick={addRow}
            className="flex items-center gap-1 px-2.5 py-1 text-[11px] bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
            style={{ fontWeight: 600, transition: "background 0.15s" }}
          >
            <Plus className="w-3 h-3" />
            {"\u05E9\u05D5\u05E8\u05D4"}
          </button>
          <button
            type="button"
            onClick={addColumn}
            className="flex items-center gap-1 px-2.5 py-1 text-[11px] bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
            style={{ fontWeight: 600, transition: "background 0.15s" }}
          >
            <Plus className="w-3 h-3" />
            {"\u05E2\u05DE\u05D5\u05D3\u05D4"}
          </button>
          {colCount > 2 && (
            <button
              type="button"
              onClick={() => removeColumn(colCount - 1)}
              className="flex items-center gap-1 px-2.5 py-1 text-[11px] bg-red-50 text-red-500 rounded-lg hover:bg-red-100"
              style={{ fontWeight: 600, transition: "background 0.15s" }}
            >
              <Trash2 className="w-3 h-3" />
              {"\u05E2\u05DE\u05D5\u05D3\u05D4 \u05D0\u05D7\u05E8\u05D5\u05E0\u05D4"}
            </button>
          )}
          <label className="flex items-center gap-1.5 text-[11px] text-gray-500 mr-auto" style={{ fontWeight: 500 }}>
            <input
              type="checkbox"
              checked={headerColumn}
              onChange={(e) => onUpdateMeta({ headerColumn: e.target.checked })}
              className="accent-blue-600 w-3.5 h-3.5"
            />
            {"\u05E2\u05DE\u05D5\u05D3\u05EA \u05DB\u05D5\u05EA\u05E8\u05EA"}
          </label>
          {headerColumn && (
            <label className="flex items-center gap-1.5 text-[11px] text-gray-500" style={{ fontWeight: 500 }}>
              {"\u05E6\u05D1\u05E2:"}
              <input
                type="color"
                value={headerColor}
                onChange={(e) => onUpdateMeta({ headerColor: e.target.value })}
                className="w-5 h-5 rounded border border-gray-200 cursor-pointer"
                style={{ padding: 0 }}
              />
            </label>
          )}
        </div>
      )}

      {/* Table */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm" dir="rtl">
          <tbody>
            {rows.map((row: { cells: string[] }, rowIdx: number) => (
              <tr
                key={rowIdx}
                className={rowIdx % 2 === 0 ? "bg-white" : "bg-gray-50/60"}
              >
                {row.cells.map((cell: string, colIdx: number) => {
                  const isHeader = headerColumn && colIdx === 0;
                  return (
                    <td
                      key={colIdx}
                      className={`px-5 py-4 border-b border-gray-100 ${
                        colIdx < row.cells.length - 1 ? "border-l border-gray-100" : ""
                      }`}
                      style={{
                        width: isHeader ? "180px" : undefined,
                        verticalAlign: "top",
                      }}
                    >
                      {isEditMode ? (
                        <textarea
                          value={cell}
                          onChange={(e) => updateCell(rowIdx, colIdx, e.target.value)}
                          className={`w-full bg-transparent outline-none resize-none text-sm leading-relaxed ${
                            isHeader ? "" : "text-gray-600"
                          }`}
                          style={{
                            fontWeight: isHeader ? 700 : 400,
                            color: isHeader ? headerColor : undefined,
                            minHeight: "28px",
                          }}
                          rows={1}
                          dir="rtl"
                          onInput={(e) => {
                            const t = e.target as HTMLTextAreaElement;
                            t.style.height = "auto";
                            t.style.height = t.scrollHeight + "px";
                          }}
                        />
                      ) : (
                        <span
                          className={`block text-sm leading-relaxed ${
                            isHeader ? "" : "text-gray-600"
                          }`}
                          style={{
                            fontWeight: isHeader ? 700 : 400,
                            color: isHeader ? headerColor : undefined,
                          }}
                        >
                          {cell}
                        </span>
                      )}
                    </td>
                  );
                })}
                {/* Row delete button */}
                {isEditMode && rows.length > 1 && (
                  <td className="w-8 border-b border-gray-100 text-center align-middle">
                    <button
                      type="button"
                      onClick={() => removeRow(rowIdx)}
                      className="p-1 text-gray-300 hover:text-red-500 rounded"
                      style={{ transition: "color 0.15s" }}
                      title={"\u05DE\u05D7\u05E7 \u05E9\u05D5\u05E8\u05D4"}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Tabs ── */
function TabsBlock({
  block,
  isEditMode,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const tabs: { id: string; title: string; content: string }[] = block.meta?.tabs || [];
  const [activeId, setActiveId] = useState(block.meta?.activeTab || tabs[0]?.id || "");

  const activeTab = tabs.find((t) => t.id === activeId) || tabs[0];

  const updateTab = (tabId: string, field: "title" | "content", value: string) => {
    const next = tabs.map((t) => (t.id === tabId ? { ...t, [field]: value } : t));
    onUpdateMeta({ tabs: next, activeTab: activeId });
  };

  const addTab = () => {
    const newId = `t${Date.now()}`;
    const next = [...tabs, { id: newId, title: `\u05D8\u05D0\u05D1 ${tabs.length + 1}`, content: "\u05EA\u05D5\u05DB\u05DF \u05D7\u05D3\u05E9 \u2014 \u05DC\u05D7\u05E6\u05D5 \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4" }];
    onUpdateMeta({ tabs: next, activeTab: newId });
    setActiveId(newId);
  };

  const removeTab = (tabId: string) => {
    if (tabs.length <= 1) return;
    const next = tabs.filter((t) => t.id !== tabId);
    const newActive = tabId === activeId ? next[0]?.id : activeId;
    onUpdateMeta({ tabs: next, activeTab: newActive });
    setActiveId(newActive);
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden" dir="rtl">
      {/* Tab headers */}
      <div className="flex border-b border-gray-200 bg-gray-50 overflow-x-auto">
        {tabs.map((tab) => (
          <div key={tab.id} className="relative group/tab flex-shrink-0">
            <button
              type="button"
              onClick={() => setActiveId(tab.id)}
              className={`px-5 py-3 text-sm whitespace-nowrap transition-colors ${
                tab.id === activeId
                  ? "bg-white text-zap-navy border-b-2 border-zap"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              }`}
              style={{ fontWeight: tab.id === activeId ? 600 : 400 }}
            >
              {isEditMode ? (
                <input
                  type="text"
                  value={tab.title}
                  onChange={(e) => updateTab(tab.id, "title", e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="bg-transparent outline-none text-center min-w-[60px] max-w-[150px]"
                  dir="rtl"
                />
              ) : (
                tab.title
              )}
            </button>
            {isEditMode && tabs.length > 1 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeTab(tab.id); }}
                className="btnReset absolute -top-1 -left-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/tab:opacity-100 text-[9px]"
                style={{ fontWeight: 700, lineHeight: 1 }}
              >
                ×
              </button>
            )}
          </div>
        ))}
        {isEditMode && (
          <button
            type="button"
            onClick={addTab}
            className="btnReset px-3 py-3 text-gray-400 hover:text-zap hover:bg-gray-100 transition-colors text-sm flex-shrink-0"
            title={"\u05D4\u05D5\u05E1\u05E3 \u05D8\u05D0\u05D1"}
          >
            +
          </button>
        )}
      </div>
      {/* Tab content */}
      <div className="p-5 min-h-[80px]">
        {activeTab && (
          <EditableBlockContent
            content={activeTab.content}
            onSave={(val) => updateTab(activeTab.id, "content", val)}
            isEditMode={isEditMode}
            tag="div"
            className="text-gray-700 text-sm md:text-base leading-relaxed"
            placeholder={"\u05EA\u05D5\u05DB\u05DF \u05D4\u05D8\u05D0\u05D1..."}
          />
        )}
      </div>
    </div>
  );
}

/* ── Timeline ── */
function TimelineBlock({
  block,
  isEditMode,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const items: { id: string; year: string; title: string; description: string }[] = block.meta?.items || [];

  const updateItem = (itemId: string, field: string, value: string) => {
    const next = items.map((it) => (it.id === itemId ? { ...it, [field]: value } : it));
    onUpdateMeta({ items: next });
  };

  const addItem = () => {
    const newId = `tl${Date.now()}`;
    onUpdateMeta({ items: [...items, { id: newId, year: "20XX", title: "\u05E9\u05DC\u05D1 \u05D7\u05D3\u05E9", description: "\u05EA\u05D9\u05D0\u05D5\u05E8 \u2014 \u05DC\u05D7\u05E6\u05D5 \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4" }] });
  };

  const removeItem = (itemId: string) => {
    if (items.length <= 1) return;
    onUpdateMeta({ items: items.filter((it) => it.id !== itemId) });
  };

  return (
    <div className="relative py-2" dir="rtl">
      {/* Vertical line */}
      <div className="absolute right-5 top-0 bottom-0 w-0.5 bg-gradient-to-b from-zap/60 via-zap/30 to-transparent" />

      <div className="space-y-6">
        {items.map((item, idx) => (
          <div key={item.id} className="relative flex items-start gap-4 group/tl">
            {/* Dot */}
            <div className="relative z-10 w-10 h-10 rounded-full bg-white border-2 border-zap flex items-center justify-center shrink-0">
              <Circle className="w-3 h-3 text-zap fill-zap" />
            </div>
            {/* Content */}
            <div className="flex-1 bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-1">
                {isEditMode ? (
                  <input
                    type="text"
                    value={item.year}
                    onChange={(e) => updateItem(item.id, "year", e.target.value)}
                    className="text-xs text-zap bg-zap-pale/50 rounded-full px-2.5 py-0.5 outline-none w-16 text-center"
                    style={{ fontWeight: 700 }}
                  />
                ) : (
                  <span className="text-xs text-zap bg-zap-pale/50 rounded-full px-2.5 py-0.5" style={{ fontWeight: 700 }}>{item.year}</span>
                )}
                {isEditMode ? (
                  <input
                    type="text"
                    value={item.title}
                    onChange={(e) => updateItem(item.id, "title", e.target.value)}
                    className="text-sm text-zap-navy bg-transparent outline-none flex-1"
                    style={{ fontWeight: 600 }}
                    dir="rtl"
                  />
                ) : (
                  <h4 className="text-sm text-zap-navy" style={{ fontWeight: 600 }}>{item.title}</h4>
                )}
              </div>
              <EditableBlockContent
                content={item.description}
                onSave={(val) => updateItem(item.id, "description", val)}
                isEditMode={isEditMode}
                tag="p"
                className="text-gray-500 text-sm leading-relaxed"
                placeholder={"\u05EA\u05D9\u05D0\u05D5\u05E8..."}
              />
              {isEditMode && items.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeItem(item.id)}
                  className="btnReset absolute top-2 left-2 w-5 h-5 bg-red-50 text-red-400 rounded-full flex items-center justify-center opacity-0 group-hover/tl:opacity-100 hover:bg-red-100 hover:text-red-600"
                  style={{ transition: "opacity 0.15s" }}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {isEditMode && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={addItem}
            className="btnReset inline-flex items-center gap-1.5 px-4 py-2 text-xs text-zap bg-zap-pale/60 rounded-lg hover:bg-zap-pale transition-colors"
            style={{ fontWeight: 500 }}
          >
            <Plus className="w-3.5 h-3.5" />
            {"\u05D4\u05D5\u05E1\u05E3 \u05E9\u05DC\u05D1"}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Pricing Cards ── */
function PricingCardsBlock({
  block,
  isEditMode,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const cards: { id: string; title: string; price: string; period: string; features: string[]; highlighted: boolean }[] = block.meta?.cards || [];

  const updateCard = (cardId: string, field: string, value: any) => {
    const next = cards.map((c) => (c.id === cardId ? { ...c, [field]: value } : c));
    onUpdateMeta({ cards: next });
  };

  const updateFeature = (cardId: string, featureIdx: number, value: string) => {
    const next = cards.map((c) => {
      if (c.id !== cardId) return c;
      const feats = [...c.features];
      feats[featureIdx] = value;
      return { ...c, features: feats };
    });
    onUpdateMeta({ cards: next });
  };

  const addFeature = (cardId: string) => {
    const next = cards.map((c) => {
      if (c.id !== cardId) return c;
      return { ...c, features: [...c.features, "\u05E4\u05D9\u05E6'\u05E8 \u05D7\u05D3\u05E9"] };
    });
    onUpdateMeta({ cards: next });
  };

  const removeFeature = (cardId: string, featureIdx: number) => {
    const next = cards.map((c) => {
      if (c.id !== cardId) return c;
      return { ...c, features: c.features.filter((_, i) => i !== featureIdx) };
    });
    onUpdateMeta({ cards: next });
  };

  const addCard = () => {
    const newId = `p${Date.now()}`;
    onUpdateMeta({ cards: [...cards, { id: newId, title: "\u05DE\u05E1\u05DC\u05D5\u05DC \u05D7\u05D3\u05E9", price: "\u20AA0", period: "\u05DC\u05D7\u05D5\u05D3\u05E9", features: ["\u05E4\u05D9\u05E6'\u05E8 \u05E8\u05D0\u05E9\u05D5\u05DF"], highlighted: false }] });
  };

  const removeCard = (cardId: string) => {
    if (cards.length <= 1) return;
    onUpdateMeta({ cards: cards.filter((c) => c.id !== cardId) });
  };

  return (
    <div dir="rtl">
      <div className={`grid gap-4 ${cards.length >= 3 ? "grid-cols-1 md:grid-cols-3" : cards.length === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 max-w-sm mx-auto"}`}>
        {cards.map((card) => (
          <div
            key={card.id}
            className={`relative rounded-xl border-2 p-5 flex flex-col group/pc transition-shadow ${
              card.highlighted
                ? "border-zap bg-zap-pale/20 shadow-lg"
                : "border-gray-200 bg-white hover:shadow-md"
            }`}
          >
            {/* Highlighted badge */}
            {card.highlighted && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-zap text-white text-[10px] px-3 py-0.5 rounded-full" style={{ fontWeight: 700 }}>
                {"\u05DE\u05D5\u05DE\u05DC\u05E5"}
              </div>
            )}

            {/* Title */}
            {isEditMode ? (
              <input
                type="text"
                value={card.title}
                onChange={(e) => updateCard(card.id, "title", e.target.value)}
                className="text-lg text-zap-navy bg-transparent outline-none text-center mb-2"
                style={{ fontWeight: 700 }}
                dir="rtl"
              />
            ) : (
              <h4 className="text-lg text-zap-navy text-center mb-2" style={{ fontWeight: 700 }}>{card.title}</h4>
            )}

            {/* Price */}
            <div className="text-center mb-1">
              {isEditMode ? (
                <input
                  type="text"
                  value={card.price}
                  onChange={(e) => updateCard(card.id, "price", e.target.value)}
                  className="text-3xl text-zap bg-transparent outline-none text-center w-32 mx-auto"
                  style={{ fontWeight: 800 }}
                />
              ) : (
                <span className="text-3xl text-zap" style={{ fontWeight: 800 }}>{card.price}</span>
              )}
            </div>
            {isEditMode ? (
              <input
                type="text"
                value={card.period}
                onChange={(e) => updateCard(card.id, "period", e.target.value)}
                className="text-xs text-gray-400 bg-transparent outline-none text-center mb-4"
              />
            ) : (
              <p className="text-xs text-gray-400 text-center mb-4">{card.period}</p>
            )}

            {/* Features */}
            <ul className="space-y-2 flex-1 mb-4">
              {card.features.map((feat, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-gray-600 group/feat">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  {isEditMode ? (
                    <input
                      type="text"
                      value={feat}
                      onChange={(e) => updateFeature(card.id, idx, e.target.value)}
                      className="bg-transparent outline-none flex-1"
                      dir="rtl"
                    />
                  ) : (
                    <span>{feat}</span>
                  )}
                  {isEditMode && card.features.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeFeature(card.id, idx)}
                      className="btnReset w-4 h-4 text-gray-300 hover:text-red-500 opacity-0 group-hover/feat:opacity-100 shrink-0"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </li>
              ))}
            </ul>

            {isEditMode && (
              <div className="flex items-center justify-between mb-3 gap-2">
                <button
                  type="button"
                  onClick={() => addFeature(card.id)}
                  className="btnReset text-[11px] text-zap hover:underline"
                >
                  + {"\u05E4\u05D9\u05E6'\u05E8"}
                </button>
                <button
                  type="button"
                  onClick={() => updateCard(card.id, "highlighted", !card.highlighted)}
                  className={`btnReset text-[11px] px-2 py-0.5 rounded ${card.highlighted ? "bg-zap text-white" : "bg-gray-100 text-gray-500"}`}
                >
                  {card.highlighted ? "\u2605 \u05DE\u05D5\u05DE\u05DC\u05E5" : "\u2606 \u05D4\u05DE\u05DC\u05E5"}
                </button>
              </div>
            )}

            {/* CTA placeholder */}
            <div className={`text-center py-2.5 rounded-lg text-sm ${
              card.highlighted
                ? "bg-zap text-white"
                : "bg-gray-100 text-gray-600"
            }`} style={{ fontWeight: 600 }}>
              {"\u05D1\u05D7\u05E8\u05D5 \u05DE\u05E1\u05DC\u05D5\u05DC"}
            </div>

            {/* Remove card */}
            {isEditMode && cards.length > 1 && (
              <button
                type="button"
                onClick={() => removeCard(card.id)}
                className="btnReset absolute top-2 left-2 w-5 h-5 bg-red-50 text-red-400 rounded-full flex items-center justify-center opacity-0 group-hover/pc:opacity-100 hover:bg-red-100 hover:text-red-600"
                style={{ transition: "opacity 0.15s" }}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
      </div>

      {isEditMode && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={addCard}
            className="btnReset inline-flex items-center gap-1.5 px-4 py-2 text-xs text-zap bg-zap-pale/60 rounded-lg hover:bg-zap-pale transition-colors"
            style={{ fontWeight: 500 }}
          >
            <Plus className="w-3.5 h-3.5" />
            {"\u05D4\u05D5\u05E1\u05E3 \u05DB\u05E8\u05D8\u05D9\u05E1"}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Toggle List ── */
function ToggleListBlock({
  block,
  isEditMode,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const items: { id: string; text: string; checked: boolean }[] = block.meta?.items || [];

  const toggleItem = (itemId: string) => {
    const next = items.map((it) => (it.id === itemId ? { ...it, checked: !it.checked } : it));
    onUpdateMeta({ items: next });
  };

  const updateItemText = (itemId: string, text: string) => {
    const next = items.map((it) => (it.id === itemId ? { ...it, text } : it));
    onUpdateMeta({ items: next });
  };

  const addItem = () => {
    const newId = `tg${Date.now()}`;
    onUpdateMeta({ items: [...items, { id: newId, text: "\u05DE\u05E9\u05D9\u05DE\u05D4 \u05D7\u05D3\u05E9\u05D4 \u2014 \u05DC\u05D7\u05E6\u05D5 \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4", checked: false }] });
  };

  const removeItem = (itemId: string) => {
    if (items.length <= 1) return;
    onUpdateMeta({ items: items.filter((it) => it.id !== itemId) });
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden" dir="rtl">
      <ul className="divide-y divide-gray-100">
        {items.map((item) => (
          <li key={item.id} className="flex items-center gap-3 px-4 py-3 group/tg hover:bg-gray-50 transition-colors">
            <button
              type="button"
              onClick={() => toggleItem(item.id)}
              className={`btnReset w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                item.checked
                  ? "bg-emerald-500 border-emerald-500"
                  : "border-gray-300 hover:border-emerald-400"
              }`}
            >
              {item.checked && <Check className="w-3 h-3 text-white" />}
            </button>
            {isEditMode ? (
              <input
                type="text"
                value={item.text}
                onChange={(e) => updateItemText(item.id, e.target.value)}
                className={`flex-1 bg-transparent outline-none text-sm ${
                  item.checked ? "line-through text-gray-400" : "text-gray-700"
                }`}
                dir="rtl"
              />
            ) : (
              <span className={`flex-1 text-sm ${
                item.checked ? "line-through text-gray-400" : "text-gray-700"
              }`}>
                {item.text}
              </span>
            )}
            {isEditMode && items.length > 1 && (
              <button
                type="button"
                onClick={() => removeItem(item.id)}
                className="btnReset w-5 h-5 text-gray-300 hover:text-red-500 opacity-0 group-hover/tg:opacity-100 shrink-0"
                style={{ transition: "opacity 0.15s" }}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </li>
        ))}
      </ul>
      {isEditMode && (
        <div className="border-t border-gray-100 px-4 py-2">
          <button
            type="button"
            onClick={addItem}
            className="btnReset inline-flex items-center gap-1.5 text-xs text-zap hover:underline"
            style={{ fontWeight: 500 }}
          >
            <Plus className="w-3.5 h-3.5" />
            {"\u05D4\u05D5\u05E1\u05E3 \u05E4\u05E8\u05D9\u05D8"}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Alert Banner ── */
function AlertBannerBlock({
  block,
  isEditMode,
  onUpdateContent,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateContent: (c: string) => void;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const variant = block.meta?.variant || "info";

  const variants: Record<string, { bg: string; border: string; icon: string; iconColor: string; IconComp: typeof Info }> = {
    info: { bg: "bg-blue-50", border: "border-blue-200", icon: "info", iconColor: "text-blue-500", IconComp: Info },
    warning: { bg: "bg-yellow-50", border: "border-yellow-200", icon: "warning", iconColor: "text-yellow-500", IconComp: AlertTriangle },
    success: { bg: "bg-emerald-50", border: "border-emerald-200", icon: "success", iconColor: "text-emerald-500", IconComp: CheckCircle2 },
    error: { bg: "bg-red-50", border: "border-red-200", icon: "error", iconColor: "text-red-500", IconComp: Bell },
    tip: { bg: "bg-purple-50", border: "border-purple-200", icon: "tip", iconColor: "text-purple-500", IconComp: Lightbulb },
  };

  const v = variants[variant] || variants.info;
  const VIcon = v.IconComp;

  const cycleVariant = () => {
    const keys = Object.keys(variants);
    const idx = keys.indexOf(variant);
    const next = keys[(idx + 1) % keys.length];
    onUpdateMeta({ variant: next });
  };

  return (
    <div className={`flex items-start gap-3 px-5 py-4 rounded-xl border ${v.bg} ${v.border}`} dir="rtl">
      <VIcon className={`w-5 h-5 shrink-0 mt-0.5 ${v.iconColor}`} />
      <div className="flex-1">
        <EditableBlockContent
          content={block.content}
          onSave={onUpdateContent}
          isEditMode={isEditMode}
          tag="p"
          className="text-gray-700 text-sm md:text-base leading-relaxed"
          placeholder={"\u05D8\u05E7\u05E1\u05D8 \u05D4\u05EA\u05E8\u05D0\u05D4..."}
        />
      </div>
      {isEditMode && (
        <button
          type="button"
          onClick={cycleVariant}
          className="btnReset text-[10px] text-gray-400 border border-gray-200 rounded px-2 py-1 hover:bg-white shrink-0 whitespace-nowrap"
          style={{ fontWeight: 500 }}
          title={"\u05D4\u05D7\u05DC\u05E3 \u05E1\u05D2\u05E0\u05D5\u05DF"}
        >
          {variant === "info" ? "\u2139\uFE0F \u05DE\u05D9\u05D3\u05E2" : variant === "warning" ? "\u26A0\uFE0F \u05D0\u05D6\u05D4\u05E8\u05D4" : variant === "success" ? "\u2705 \u05D4\u05E6\u05DC\u05D7\u05D4" : variant === "error" ? "\u274C \u05E9\u05D2\u05D9\u05D0\u05D4" : "\u05D2 \u05D8\u05D9\u05E4"}
        </button>
      )}
    </div>
  );
}

/* ── Accordion ── */
function AccordionBlock({
  block,
  isEditMode,
  onUpdateContent,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateContent: (c: string) => void;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const answer = block.meta?.answer || "";

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-5 py-4 bg-gray-50 hover:bg-gray-100 transition-colors text-right"
      >
        <EditableBlockContent
          content={block.content}
          onSave={onUpdateContent}
          isEditMode={isEditMode}
          tag="span"
          className="text-zap-navy text-sm md:text-base flex-1"
          placeholder="שאלה..."
        />
        <ChevronDown
          className={`w-5 h-5 text-gray-400 shrink-0 mr-3 transition-transform ${isExpanded ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 py-4 border-t border-gray-100">
              <EditableBlockContent
                content={answer}
                onSave={(val) => onUpdateMeta({ answer: val })}
                isEditMode={isEditMode}
                tag="p"
                className="text-gray-600 text-sm leading-relaxed"
                placeholder="תשובה..."
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Team Cards ── */
function TeamCardsBlock({
  block,
  isEditMode,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const members: { id: string; name: string; role: string; imageUrl: string; bio: string }[] = block.meta?.members || [];

  const updateMember = (memberId: string, field: string, value: string) => {
    const next = members.map((m) => (m.id === memberId ? { ...m, [field]: value } : m));
    onUpdateMeta({ members: next });
  };

  const addMember = () => {
    const newId = `tm${Date.now()}`;
    onUpdateMeta({
      members: [...members, { id: newId, name: "\u05E9\u05DD \u05D7\u05D3\u05E9", role: "\u05EA\u05E4\u05E7\u05D9\u05D3", imageUrl: "", bio: "\u05EA\u05D9\u05D0\u05D5\u05E8 \u05E7\u05E6\u05E8 \u2014 \u05DC\u05D7\u05E6\u05D5 \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4" }],
    });
  };

  const removeMember = (memberId: string) => {
    if (members.length <= 1) return;
    onUpdateMeta({ members: members.filter((m) => m.id !== memberId) });
  };

  return (
    <div dir="rtl">
      <div className={`grid gap-6 ${members.length >= 3 ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" : members.length === 2 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 max-w-xs mx-auto"}`}>
        {members.map((member) => (
          <div
            key={member.id}
            className="relative bg-white rounded-xl border border-gray-200 overflow-hidden text-center group/tm hover:shadow-lg"
            style={{ transition: "box-shadow 0.2s" }}
          >
            {/* Avatar area */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 pt-8 pb-4 flex justify-center">
              {member.imageUrl ? (
                <EditableImage
                  src={member.imageUrl}
                  alt={member.name}
                  className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-md"
                  isEditMode={isEditMode}
                  onImageChange={(url) => updateMember(member.id, "imageUrl", url)}
                />
              ) : (
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-white border-4 border-white shadow-md flex items-center justify-center">
                    <Users className="w-10 h-10 text-gray-300" />
                  </div>
                  {isEditMode && (
                    <EditableImage
                      src=""
                      alt={member.name}
                      className="absolute inset-0 w-24 h-24 rounded-full opacity-0 cursor-pointer"
                      isEditMode={isEditMode}
                      onImageChange={(url) => updateMember(member.id, "imageUrl", url)}
                    />
                  )}
                </div>
              )}
            </div>

            {/* Content */}
            <div className="px-5 py-4 space-y-1.5">
              {isEditMode ? (
                <input
                  type="text"
                  value={member.name}
                  onChange={(e) => updateMember(member.id, "name", e.target.value)}
                  className="text-lg text-zap-navy bg-transparent outline-none text-center w-full"
                  style={{ fontWeight: 700 }}
                  dir="rtl"
                />
              ) : (
                <h4 className="text-lg text-zap-navy" style={{ fontWeight: 700 }}>{member.name}</h4>
              )}
              {isEditMode ? (
                <input
                  type="text"
                  value={member.role}
                  onChange={(e) => updateMember(member.id, "role", e.target.value)}
                  className="text-sm text-zap bg-transparent outline-none text-center w-full"
                  style={{ fontWeight: 500 }}
                  dir="rtl"
                />
              ) : (
                <p className="text-sm text-zap" style={{ fontWeight: 500 }}>{member.role}</p>
              )}
              {isEditMode ? (
                <textarea
                  value={member.bio}
                  onChange={(e) => updateMember(member.id, "bio", e.target.value)}
                  className="text-xs text-gray-500 bg-transparent outline-none text-center w-full resize-none leading-relaxed mt-1"
                  rows={2}
                  dir="rtl"
                />
              ) : (
                <p className="text-xs text-gray-500 leading-relaxed mt-1">{member.bio}</p>
              )}
            </div>

            {/* Remove button */}
            {isEditMode && members.length > 1 && (
              <button
                type="button"
                onClick={() => removeMember(member.id)}
                className="btnReset absolute top-2 left-2 w-5 h-5 bg-red-50 text-red-400 rounded-full flex items-center justify-center opacity-0 group-hover/tm:opacity-100 hover:bg-red-100 hover:text-red-600"
                style={{ transition: "opacity 0.15s" }}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
      </div>

      {isEditMode && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={addMember}
            className="btnReset inline-flex items-center gap-1.5 px-4 py-2 text-xs text-zap bg-zap-pale/60 rounded-lg hover:bg-zap-pale transition-colors"
            style={{ fontWeight: 500 }}
          >
            <Plus className="w-3.5 h-3.5" />
            {"\u05D4\u05D5\u05E1\u05E3 \u05D7\u05D1\u05E8 \u05E6\u05D5\u05D5\u05EA"}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Countdown ── */
function CountdownBlock({
  block,
  isEditMode,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const targetDate = block.meta?.targetDate || "";
  const label = block.meta?.label || "";
  const style = block.meta?.style || "cards";

  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    if (!targetDate) return;
    const calc = () => {
      const now = Date.now();
      const target = new Date(targetDate).getTime();
      const diff = Math.max(0, target - now);
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    calc();
    const interval = setInterval(calc, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  const units = [
    { value: timeLeft.days, label: "\u05D9\u05DE\u05D9\u05DD" },
    { value: timeLeft.hours, label: "\u05E9\u05E2\u05D5\u05EA" },
    { value: timeLeft.minutes, label: "\u05D3\u05E7\u05D5\u05EA" },
    { value: timeLeft.seconds, label: "\u05E9\u05E0\u05D9\u05D5\u05EA" },
  ];

  return (
    <div dir="rtl" className="text-center space-y-4">
      {/* Label */}
      {isEditMode ? (
        <input
          type="text"
          value={label}
          onChange={(e) => onUpdateMeta({ label: e.target.value })}
          className="text-xl text-zap-navy bg-transparent outline-none text-center w-full"
          style={{ fontWeight: 700 }}
          dir="rtl"
          placeholder={"\u05DB\u05D5\u05EA\u05E8\u05EA \u05DC\u05E1\u05E4\u05D9\u05E8\u05D4..."}
        />
      ) : label ? (
        <h3 className="text-xl text-zap-navy" style={{ fontWeight: 700 }}>{label}</h3>
      ) : null}

      {/* Timer */}
      <div className="flex justify-center gap-3 sm:gap-5">
        {units.map((u, i) => (
          <div key={i} className="flex flex-col items-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-zap-navy to-blue-900 text-white rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-2xl sm:text-3xl tabular-nums" style={{ fontWeight: 800 }}>{String(u.value).padStart(2, "0")}</span>
            </div>
            <span className="text-[11px] text-gray-500 mt-1.5" style={{ fontWeight: 500 }}>{u.label}</span>
          </div>
        ))}
      </div>

      {/* Edit: target date */}
      {isEditMode && (
        <div className="flex items-center justify-center gap-2 mt-3">
          <Timer className="w-4 h-4 text-gray-400" />
          <label className="text-xs text-gray-500">{"\u05EA\u05D0\u05E8\u05D9\u05DA \u05D9\u05E2\u05D3:"}</label>
          <input
            type="datetime-local"
            value={targetDate}
            onChange={(e) => onUpdateMeta({ targetDate: e.target.value })}
            className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
          />
        </div>
      )}
    </div>
  );
}

/* ── Social Links ── */
function SocialLinksBlock({
  block,
  isEditMode,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const links: { id: string; platform: string; url: string; label: string }[] = block.meta?.links || [];
  const blockStyle = block.meta?.style || "rounded";

  const platformIcons: Record<string, { emoji: string; bg: string; hoverBg: string }> = {
    facebook: { emoji: "f", bg: "bg-blue-600", hoverBg: "hover:bg-blue-700" },
    instagram: { emoji: "ig", bg: "bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400", hoverBg: "hover:opacity-90" },
    whatsapp: { emoji: "wa", bg: "bg-green-500", hoverBg: "hover:bg-green-600" },
    linkedin: { emoji: "in", bg: "bg-blue-700", hoverBg: "hover:bg-blue-800" },
    twitter: { emoji: "X", bg: "bg-black", hoverBg: "hover:bg-gray-800" },
    youtube: { emoji: "\u25B6", bg: "bg-red-600", hoverBg: "hover:bg-red-700" },
    tiktok: { emoji: "tk", bg: "bg-black", hoverBg: "hover:bg-gray-800" },
    telegram: { emoji: "tg", bg: "bg-sky-500", hoverBg: "hover:bg-sky-600" },
    email: { emoji: "@", bg: "bg-gray-600", hoverBg: "hover:bg-gray-700" },
    website: { emoji: "\uD83C\uDF10", bg: "bg-indigo-500", hoverBg: "hover:bg-indigo-600" },
  };

  const platformOptions = Object.keys(platformIcons);

  const updateLink = (linkId: string, field: string, value: string) => {
    const next = links.map((l) => (l.id === linkId ? { ...l, [field]: value } : l));
    onUpdateMeta({ links: next, style: blockStyle });
  };

  const addLink = () => {
    const newId = `sl${Date.now()}`;
    onUpdateMeta({
      links: [...links, { id: newId, platform: "website", url: "https://", label: "\u05E7\u05D9\u05E9\u05D5\u05E8 \u05D7\u05D3\u05E9" }],
      style: blockStyle,
    });
  };

  const removeLink = (linkId: string) => {
    if (links.length <= 1) return;
    onUpdateMeta({ links: links.filter((l) => l.id !== linkId), style: blockStyle });
  };

  const isRounded = blockStyle === "rounded";

  return (
    <div dir="rtl" className="space-y-3">
      <div className="flex flex-wrap justify-center gap-3">
        {links.map((link) => {
          const pi = platformIcons[link.platform] || platformIcons.website;
          return (
            <div key={link.id} className="relative group/sl">
              {isEditMode ? (
                <div className="flex flex-col items-center gap-1.5 p-3 bg-gray-50 border border-gray-200 rounded-xl min-w-[100px]">
                  <select
                    value={link.platform}
                    onChange={(e) => updateLink(link.id, "platform", e.target.value)}
                    className="text-xs bg-white border border-gray-200 rounded px-1.5 py-1 w-full"
                  >
                    {platformOptions.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={link.url}
                    onChange={(e) => updateLink(link.id, "url", e.target.value)}
                    className="text-[10px] bg-white border border-gray-200 rounded px-1.5 py-1 w-full text-center"
                    placeholder="URL"
                    dir="ltr"
                  />
                  <input
                    type="text"
                    value={link.label}
                    onChange={(e) => updateLink(link.id, "label", e.target.value)}
                    className="text-[10px] bg-white border border-gray-200 rounded px-1.5 py-1 w-full text-center"
                    placeholder={"\u05EA\u05D5\u05D5\u05D9\u05EA"}
                    dir="rtl"
                  />
                  {links.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLink(link.id)}
                      className="btnReset text-[9px] text-red-400 hover:text-red-600"
                    >
                      {"\u05DE\u05D7\u05E7"}
                    </button>
                  )}
                </div>
              ) : (
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex flex-col items-center gap-1.5 group/icon`}
                  title={link.label}
                >
                  <div className={`w-12 h-12 ${isRounded ? "rounded-full" : "rounded-xl"} ${pi.bg} ${pi.hoverBg} text-white flex items-center justify-center text-sm shadow-md transition-transform hover:scale-110`} style={{ fontWeight: 700 }}>
                    {pi.emoji}
                  </div>
                  <span className="text-[11px] text-gray-500" style={{ fontWeight: 500 }}>{link.label}</span>
                </a>
              )}
            </div>
          );
        })}
      </div>

      {isEditMode && (
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={addLink}
            className="btnReset inline-flex items-center gap-1.5 px-4 py-2 text-xs text-zap bg-zap-pale/60 rounded-lg hover:bg-zap-pale transition-colors"
            style={{ fontWeight: 500 }}
          >
            <Plus className="w-3.5 h-3.5" />
            {"\u05D4\u05D5\u05E1\u05E3 \u05E7\u05D9\u05E9\u05D5\u05E8"}
          </button>
          <button
            type="button"
            onClick={() => onUpdateMeta({ links, style: isRounded ? "square" : "rounded" })}
            className="btnReset text-[11px] px-3 py-1.5 border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50"
          >
            {isRounded ? "\u25A0 \u05DE\u05E8\u05D5\u05D1\u05E2" : "\u25CF \u05E2\u05D2\u05D5\u05DC"}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Progress Bars ── */
function ProgressBarsBlock({
  block,
  isEditMode,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const bars: { id: string; label: string; value: number }[] = block.meta?.bars || [];
  const color = block.meta?.color || "#1a73e8";

  const updateBar = (barId: string, field: string, value: string | number) => {
    const next = bars.map((b) => (b.id === barId ? { ...b, [field]: value } : b));
    onUpdateMeta({ bars: next, color });
  };

  const addBar = () => {
    const newId = `pb${Date.now()}`;
    onUpdateMeta({ bars: [...bars, { id: newId, label: "\u05DE\u05D9\u05D5\u05DE\u05E0\u05D5\u05EA \u05D7\u05D3\u05E9\u05D4", value: 70 }], color });
  };

  const removeBar = (barId: string) => {
    if (bars.length <= 1) return;
    onUpdateMeta({ bars: bars.filter((b) => b.id !== barId), color });
  };

  return (
    <div dir="rtl" className="space-y-4">
      {bars.map((bar) => (
        <div key={bar.id} className="group/pb">
          <div className="flex items-center justify-between mb-1.5">
            {isEditMode ? (
              <input
                type="text"
                value={bar.label}
                onChange={(e) => updateBar(bar.id, "label", e.target.value)}
                className="text-sm text-zap-navy bg-transparent outline-none flex-1"
                style={{ fontWeight: 600 }}
                dir="rtl"
              />
            ) : (
              <span className="text-sm text-zap-navy" style={{ fontWeight: 600 }}>{bar.label}</span>
            )}
            <div className="flex items-center gap-2">
              {isEditMode ? (
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={bar.value}
                  onChange={(e) => updateBar(bar.id, "value", Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                  className="w-14 text-xs text-center border border-gray-200 rounded px-1 py-0.5 bg-white"
                />
              ) : null}
              <span className="text-sm text-gray-500 tabular-nums" style={{ fontWeight: 600 }}>{bar.value}%</span>
              {isEditMode && bars.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeBar(bar.id)}
                  className="btnReset w-4 h-4 text-gray-300 hover:text-red-500 opacity-0 group-hover/pb:opacity-100"
                  style={{ transition: "opacity 0.15s" }}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
          <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700 ease-out"
              style={{ width: `${bar.value}%`, backgroundColor: color }}
            />
          </div>
        </div>
      ))}

      {isEditMode && (
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={addBar}
            className="btnReset inline-flex items-center gap-1.5 px-4 py-2 text-xs text-zap bg-zap-pale/60 rounded-lg hover:bg-zap-pale transition-colors"
            style={{ fontWeight: 500 }}
          >
            <Plus className="w-3.5 h-3.5" />
            {"\u05D4\u05D5\u05E1\u05E3 \u05E1\u05E8\u05D2\u05DC"}
          </button>
          <div className="flex items-center gap-1.5">
            <label className="text-[11px] text-gray-400">{"\u05E6\u05D1\u05E2:"}</label>
            <input
              type="color"
              value={color}
              onChange={(e) => onUpdateMeta({ bars, color: e.target.value })}
              className="w-6 h-6 rounded border border-gray-200 cursor-pointer"
            />
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Marquee ── */
function MarqueeBlock({
  block,
  isEditMode,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const items: string[] = block.meta?.items || [];
  const speed = block.meta?.speed || 30;
  const bgColor = block.meta?.bgColor || "#1a2340";
  const textColor = block.meta?.textColor || "#ffffff";
  const [paused, setPaused] = useState(false);

  const updateItem = (idx: number, value: string) => {
    const next = [...items];
    next[idx] = value;
    onUpdateMeta({ items: next, speed, bgColor, textColor });
  };

  const addItem = () => {
    onUpdateMeta({ items: [...items, "\u05D8\u05E7\u05E1\u05D8 \u05D7\u05D3\u05E9 \u2014 \u05DC\u05D7\u05E6\u05D5 \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4"], speed, bgColor, textColor });
  };

  const removeItem = (idx: number) => {
    if (items.length <= 1) return;
    onUpdateMeta({ items: items.filter((_, i) => i !== idx), speed, bgColor, textColor });
  };

  if (isEditMode) {
    return (
      <div dir="rtl" className="space-y-3">
        {/* Preview */}
        <div className="rounded-lg overflow-hidden" style={{ backgroundColor: bgColor }}>
          <div className="py-3 px-4 overflow-hidden whitespace-nowrap">
            <span style={{ color: textColor }} className="text-sm">
              {items.join("  \u00B7  ")}
            </span>
          </div>
        </div>
        {/* Edit items */}
        <div className="space-y-2">
          {items.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 group/mq">
              <span className="text-[10px] text-gray-400 w-5 text-center">{idx + 1}</span>
              <input
                type="text"
                value={item}
                onChange={(e) => updateItem(idx, e.target.value)}
                className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white"
                dir="rtl"
              />
              {items.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeItem(idx)}
                  className="btnReset w-5 h-5 text-gray-300 hover:text-red-500 opacity-0 group-hover/mq:opacity-100"
                  style={{ transition: "opacity 0.15s" }}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={addItem}
            className="btnReset inline-flex items-center gap-1.5 px-4 py-2 text-xs text-zap bg-zap-pale/60 rounded-lg hover:bg-zap-pale transition-colors"
            style={{ fontWeight: 500 }}
          >
            <Plus className="w-3.5 h-3.5" />
            {"\u05D4\u05D5\u05E1\u05E3 \u05D8\u05E7\u05E1\u05D8"}
          </button>
          <div className="flex items-center gap-1.5">
            <label className="text-[11px] text-gray-400">{"\u05E8\u05E7\u05E2:"}</label>
            <input
              type="color"
              value={bgColor}
              onChange={(e) => onUpdateMeta({ items, speed, bgColor: e.target.value, textColor })}
              className="w-6 h-6 rounded border border-gray-200 cursor-pointer"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-[11px] text-gray-400">{"\u05D8\u05E7\u05E1\u05D8:"}</label>
            <input
              type="color"
              value={textColor}
              onChange={(e) => onUpdateMeta({ items, speed, bgColor, textColor: e.target.value })}
              className="w-6 h-6 rounded border border-gray-200 cursor-pointer"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-[11px] text-gray-400">{"\u05DE\u05D4\u05D9\u05E8\u05D5\u05EA:"}</label>
            <input
              type="range"
              min={10}
              max={80}
              value={speed}
              onChange={(e) => onUpdateMeta({ items, speed: parseInt(e.target.value), bgColor, textColor })}
              className="w-20 h-1"
            />
          </div>
        </div>
      </div>
    );
  }

  // View mode — scrolling marquee
  const marqueeText = items.join("     \u00B7     ");
  return (
    <div
      className="rounded-lg overflow-hidden cursor-pointer relative group/mqv"
      style={{ backgroundColor: bgColor }}
      onClick={() => setPaused(!paused)}
    >
      <div className="py-3 overflow-hidden whitespace-nowrap">
        <div
          className="inline-block animate-marquee"
          style={{
            animationDuration: `${speed}s`,
            animationPlayState: paused ? "paused" : "running",
            color: textColor,
          }}
        >
          <span className="text-sm inline-block px-8" style={{ fontWeight: 500 }}>
            {marqueeText}
          </span>
          <span className="text-sm inline-block px-8" style={{ fontWeight: 500 }}>
            {marqueeText}
          </span>
        </div>
      </div>
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee linear infinite;
        }
      `}</style>
    </div>
  );
}

/* ───────────── ContentBlockZone (main export) ───────────── */

export function ContentBlockZone({
  zoneId = "bottom",
  inline = false,
  emptyLabel,
  dropOnly = false,
}: {
  zoneId?: string;
  inline?: boolean;
  emptyLabel?: string;
  dropOnly?: boolean;
}) {
  const {
    blocks,
    isEditMode,
    addBlock,
    removeBlock,
    updateContent,
    updateVariant,
    updateMeta,
    moveBlock,
    moveBlockToIndex,
    duplicateBlock,
  } = useBlocks(zoneId);

  const [showAddMenu, setShowAddMenu] = useState<number | null>(null);

  /* Detect global drag state for showing prominent drop targets */
  const { isDragging: isDragActive } = useIsDraggingAny();

  const handleDropPalette = useCallback(
    (blockType: string, index: number, savedContent?: string, savedMeta?: Record<string, any>, savedVariant?: string) => {
      addBlock(blockType, index, savedContent, savedMeta, savedVariant);
    },
    [addBlock]
  );

  const handleDropBlock = useCallback(
    (blockId: string, index: number) => {
      moveBlockToIndex(blockId, index);
    },
    [moveBlockToIndex]
  );

  /* ─── Paste from clipboard ─── */
  const [hasClipboard, setHasClipboard] = useState(() => hasClipboardBlock());
  useEffect(() => {
    const unsub = onClipboardChange(() => setHasClipboard(hasClipboardBlock()));
    // Also poll on focus (in case clipboard was set from another tab/template)
    const onFocus = () => setHasClipboard(hasClipboardBlock());
    window.addEventListener("focus", onFocus);
    return () => { unsub(); window.removeEventListener("focus", onFocus); };
  }, []);

  const handlePasteFromClipboard = useCallback((index: number) => {
    const clipBlocks = getClipboardBlocks();
    if (!clipBlocks || clipBlocks.length === 0) return;
    clipBlocks.forEach((cb, i) => {
      addBlock(cb.type, index + i, cb.content, cb.meta, cb.variant);
    });
    // Visual feedback
    const el = document.createElement("div");
    el.textContent = `\u05D4\u05D5\u05D3\u05D1\u05E7! ${clipBlocks.length} \u05D1\u05DC\u05D5\u05E7\u05D9\u05DD`;
    el.style.cssText = "position:fixed;top:20px;left:50%;transform:translateX(-50%);z-index:9999;background:#2563eb;color:#fff;padding:6px 16px;border-radius:8px;font-size:13px;font-weight:600;box-shadow:0 4px 12px rgba(0,0,0,0.15);pointer-events:none;";
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 1200);
  }, [addBlock]);

  /* dropOnly mode: when zone is empty, only show drop indicator (hidden when not dragging) */
  if (dropOnly && blocks.length === 0) {
    if (!isEditMode) return null;
    return (
      <div className="relative" data-content-block-zone={zoneId}>
        <DropIndicator
          index={0}
          onDropPalette={handleDropPalette}
          onDropBlock={handleDropBlock}
          isDragActive={isDragActive}
        />
      </div>
    );
  }

  if (!isEditMode && blocks.length === 0) return null;

  const blocksContent = (
    <>
      <AnimatePresence mode="popLayout">
        {blocks.map((block, index) => (
          <div key={block.id}>
            {/* Drop indicator before each block + insert button (edit mode) */}
            {isEditMode && (
              <>
                <DropIndicator
                  index={index}
                  onDropPalette={handleDropPalette}
                  onDropBlock={handleDropBlock}
                  isDragActive={isDragActive}
                />
                <InsertButton
                  index={index}
                  showMenu={showAddMenu === index}
                  onToggle={() => setShowAddMenu(showAddMenu === index ? null : index)}
                  onAdd={(type) => {
                    addBlock(type, index);
                    setShowAddMenu(null);
                  }}
                />
              </>
            )}
            <BlockRenderer
              block={block}
              isEditMode={isEditMode}
              onUpdateContent={(content) => updateContent(block.id, content)}
              onUpdateMeta={(meta) => updateMeta(block.id, meta)}
              onDelete={() => removeBlock(block.id)}
              onDuplicate={() => duplicateBlock(block.id)}
              onMoveUp={() => moveBlock(block.id, "up")}
              onMoveDown={() => moveBlock(block.id, "down")}
              onUpdateVariant={(variant) => updateVariant(block.id, variant)}
              isFirst={index === 0}
              isLast={index === blocks.length - 1}
            />
          </div>
        ))}
      </AnimatePresence>

      {/* Drop indicator + add button at the end */}
      {isEditMode && (
        <>
          <DropIndicator
            index={blocks.length}
            onDropPalette={handleDropPalette}
            onDropBlock={handleDropBlock}
            isDragActive={isDragActive}
          />
          <InsertButton
            index={blocks.length}
            showMenu={showAddMenu === blocks.length}
            onToggle={() => setShowAddMenu(showAddMenu === blocks.length ? null : blocks.length)}
            onAdd={(type) => {
              addBlock(type, blocks.length);
              setShowAddMenu(null);
            }}
            isEmpty={blocks.length === 0}
            emptyLabel={emptyLabel}
          />
          {/* Paste from clipboard indicator */}
          {hasClipboard && (
            <div className="flex justify-center py-1">
              <button
                type="button"
                onClick={() => handlePasteFromClipboard(blocks.length)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 transition-colors"
                style={{ fontWeight: 600 }}
                title={"\u05D4\u05D3\u05D1\u05E7 \u05DE\u05D4\u05DC\u05D5\u05D7 (\u05D1\u05DC\u05D5\u05E7 \u05DE\u05D5\u05E2\u05EA\u05E7)"}
              >
                <ClipboardPaste className="w-3.5 h-3.5" />
                {"\u05D4\u05D3\u05D1\u05E7 \u05DE\u05D4\u05DC\u05D5\u05D7"}
              </button>
            </div>
          )}
        </>
      )}
    </>
  );

  if (inline) {
    return (
      <div className={`relative ${blocks.length > 0 ? "py-4" : ""}`} data-content-block-zone={zoneId}>
        {blocksContent}
      </div>
    );
  }

  return (
    <section className="relative" data-content-block-zone={zoneId}>
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 ${blocks.length > 0 ? "py-8" : ""}`}>
        {blocksContent}
      </div>
    </section>
  );
}

/** Convenience: a drop-only inline ContentBlockZone that only shows targets during active drag */
export function DropSlot({ id }: { id: string }) {
  return <ContentBlockZone zoneId={id} inline dropOnly />;
}